import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "../../../utils/errorUtils";
import { isBooleanFalse, isBooleanTrue, isListEmpty, isListNotEmpty, isNotNullOrUndefined, isNullOrUndefined, isNumberEmpty, isNumberGreaterThanOrEqualToZero, isNumberGreaterThanZero, isNumberNotEmpty, isObjectEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../../../utils/objectUtils";
import { saveFunnelProgress } from "../../../firebase/Leads";
import { isWritableGender } from "../../../utils/validationUtils";
import logger from "../../../utils/logger";
import CartItem from "../../../models/CartItem";
import OrderShippingLine from "../../../models/OrderShippingLine";
import OrderDiscountLine from "../../../models/OrderDiscountLine";
import Coupon from "../../../models/Coupon";
import { isArray, isString, orderBy, toLower, toUpper } from "lodash";
import { asTimeZone } from "../../../utils/dateFormatter";
import moment from "moment-timezone";
import { useCallback, useMemo } from "react";
import { getSalesTax } from "../../../firebase/Ecommerce";
import { recordProfileWeight, setBMI, setDateOfBirth, setFirstName, setGender, setHeight, setLastName, setShippingState, setWeight, useAuth } from "../auth";
import { getProductFamilies, getProducts, getPrograms, getOfferGroups, getFunnelChoiceSets, getSurveys, getVariants, selectDefaultCampaignByProgramSlug, selectDefaultProductByProgramSlug, selectProductCategoryBySlug, selectProductsByProgramSlug, selectProgramBySlug } from "../orchestrator";
import { getDefaultProduct, getDefaultProgramByProductSku, getProductBySku, getProductBySlug, getProductsByProgramSlug, getProductsByProgramSlugs, getProgramBySlug, getProgramsBySlugs, getShippingMethodById, getSurveyBySlug, getVariantById, getVariantBySku } from "../../../utils/entityUtils";
import { toMaxBundleCount, toSelectableBundleCount } from "../../../utils/cartItemUtils";
import { setLead, setLeadId, updateAnalyticsFields, updateLead, useAnalytics } from "../analytics";
import Variant from "../../../models/Variant";
import { isAgeUnder18, isAgeUnder20 } from "../../../utils/validationUtils";
import { addSingleItemToCart, changeBundleCount, loadCartFromLead, useCart } from "../cart";
import Lead from "../../../models/Lead";
import { updateRecord } from "../../../utils/dbUtils";
import SiteEvent from "../../../models/SiteEvent";
import Product from "../../../models/Product";
import Subscription from "../../../models/Subscription";
import Encounter from "../../../models/Encounter";
import StatusHistory from "../../../models/StatusHistory";
import { sendFirstPromoterSignUp } from "../../../firebase/FirstPromoter";
import { reportMissingSubscriptionEncounterIssue } from "../../../firebase/SystemIntegrity";
import { DUPLICATE_BLOCKING_STATUSES, SURVEY_FLOW_KEYS, findBlockingEntityForDuplicateRule, getDuplicateProgramIds, getEffectiveProductFamilyId, getEffectiveOfferGroupId, getEffectiveOfferToken, getEffectiveSurveyForFlow, resolveOfferContext } from "../../../utils/productIdentityUtils";
import { getCanonicalProductForChoiceSetOfferGroup, getFunnelChoiceSetForEntry, isProductEligibleForFunnel } from "../../../utils/funnelChoiceSetUtils";

/**
 * Marks a lead duplicate through the funnel progress callable rather than a raw client write.
 *
 * These three sites are awaited on the Continue button's path (onSubmitState -> goToIntake -> createLead),
 * and a client write resolves only on server acknowledgement - it never times out, so a stalled one leaves
 * the customer on a dead step with no error and no way forward. The per-record write queue bounds that:
 * two of the three sites await a chain keyed on the OTHER lead's id, a fresh key with nothing queued ahead
 * of it; the third names the session's own lead and can queue behind its progress writes.
 *
 * It also puts `status` on ONE channel. Written from both the callable and a client write, a queued
 * DISQUALIFIED or CREATED could land after this DUPLICATE and revert it.
 */
const markLeadDuplicate = async (leadId, updatedLeadFields) => {
	if (isStringEmpty(leadId)) {
		return false;
	}

	try {
		const result = await saveFunnelProgress({ leadId: leadId, leadFields: { ...updatedLeadFields } });

		return isBooleanTrue(result?.lead);
	} catch (error) {
		logger.logErrorEvent('MARK_LEAD_DUPLICATE', error, getErrorMessage(error), false);

		return false;
	}
};

const getSurveyByEffectiveSurveyFlow = ({ program = null, product = null, productFamilies = [], surveys = [], flowKey = null }) => {
	return getEffectiveSurveyForFlow({ program, product, productFamilies, surveys, flowKey });
};

const getEnabledProductVariants = (product = null, variants = []) => {
	if (isObjectEmpty(product) || isListEmpty(variants)) {
		return [];
	}

	return variants.filter(i => i.productId === product.id && isBooleanTrue(i.enabled));
};

const getDefaultEnabledVariantForFunnelProduct = (product = null, variants = []) => {
	const enabledProductVariants = getEnabledProductVariants(product, variants);
	if (isListEmpty(enabledProductVariants)) {
		return null;
	}

	return enabledProductVariants.find(i => i.titrationLevel === 1 && i.titrationSchedule === 'ALTERNATIVE')
		?? enabledProductVariants.find(i => i.titrationLevel === 1)
		?? orderBy(enabledProductVariants, ['displayOrder', 'name'], ['asc', 'asc'])[0]
		?? null;
};

const getVariantForLeadCartItem = (cartItem = null, product = null, variants = []) => {
	const enabledProductVariants = getEnabledProductVariants(product, variants);
	if (isObjectEmpty(cartItem) || isListEmpty(enabledProductVariants)) {
		return getDefaultEnabledVariantForFunnelProduct(product, variants);
	}

	const sourceVariant = getVariantById(cartItem?.variantId, variants) ?? getVariantBySku(cartItem?.variantSku, variants);
	return enabledProductVariants.find(i => i.id === cartItem?.variantId)
		?? enabledProductVariants.find(i => i.sku === cartItem?.variantSku)
		?? (isStringNotEmpty(sourceVariant?.strength) ? enabledProductVariants.find(i => i.strength === sourceVariant.strength) : null)
		?? (isNumberNotEmpty(sourceVariant?.titrationLevel) ? enabledProductVariants.find(i => i.titrationLevel === sourceVariant.titrationLevel && (isStringEmpty(sourceVariant?.titrationSchedule) || i.titrationSchedule === sourceVariant.titrationSchedule)) : null)
		?? getDefaultEnabledVariantForFunnelProduct(product, variants);
};

const getLeadCartItemForResolvedProduct = ({ cartItems = [], product = null, productSku = null, effectiveOfferGroupId = null, products = [] } = {}) => {
	if (isListEmpty(cartItems) || isObjectEmpty(product)) {
		return null;
	}

	return cartItems.find(i => i?.id === product.id || i?.sku === productSku || i?.sku === product.sku)
		?? cartItems.find(i => {
			const cartProduct = getProductBySku(i?.sku, products);
			const cartOfferGroupId = getEffectiveOfferGroupId(cartProduct);
			return isObjectNotEmpty(cartProduct)
				&& isStringNotEmpty(effectiveOfferGroupId)
				&& isStringNotEmpty(cartOfferGroupId)
				&& cartOfferGroupId === effectiveOfferGroupId;
		})
		?? null;
};

const initialState = {
	addresses: [],
	selectedAddress: null,
	showAddressesModal: false,
	showNewAddressModal: false,
	showEditAddressModal: false,

	activeStep: null,
	currentSection: null,
	completedSections: [],
	goalsAnswers: [],
	intakeAnswers: [],

	weightLossAnswers: {
		maleEffects: [],
		femaleEffects: [],
		priority: null,
		bodyShape: null,
		targetWeight: null,
		primaryGoal: [],
		daySpent: null,
		activityLevel: null,
		metabolicRate: null,
		sleepQuality: null,
		sleepLength: null,
		mindset: null
	},
	currentlyPregnant: null,
	cutoffWeight: 0,
	currentlyTakingGLP1: null,
	experiencesSideEffects: null,
	wantsAlternativeDosing: null,
	avoidSideEffects: null,

	intakeSteps: [],
	activeIntakeStep: null,

	goalsSteps: [],
	activeGoalsStep: null,

	showSignInModal: false,
	showMoreInfoModal: false,
	showWeightLossDisclaimerModal: false,

	productSku: null,
	selectedProduct: null,
	selectedFrequency: 1,

	availableProductSkus: [],
	unenrolledProductSkus: [],

	selectedVariantSku: null,
	startingLevel: 1,

	subscription: null,
	encounter: null,
	order: null,

	userCouponCode: '',
	showPaymentScheduleModal: false,
	billingSameAsShipping: true,
	paymentMethodType: 'CARD',
	paymentMethods: [],
	showKlarnaPaymentMethod: false,

	isFunnelLoading: false,
	isFunnelLoadingMessage: null,
	isAccountLoading: false,
	isAccountLoadingMessage: null,
	isEligibilityLoading: false,
	isEligibilityLoadingMessage: null,
	isAddressLoading: false,
	addressError: null,
	funnelError: null,

	programSlug: null,
	addon: false,
	version: null
};

const funnelReducer = (state = initialState, action) => {
	const { type, payload } = action;
	switch (type) {


		case 'SET_ADDRESSES':
			return {
				...state,
				addresses: payload
			};
		case 'SET_SELECTED_ADDRESS':
			return {
				...state,
				selectedAddress: payload
			};
		case 'SET_SHOW_ADDRESSES_MODAL':
			return {
				...state,
				showAddressesModal: payload
			};
		case 'SET_SHOW_NEW_ADDRESS_MODAL':
			return {
				...state,
				showNewAddressModal: payload
			};
		case 'SET_SHOW_EDIT_ADDRESS_MODAL':
			return {
				...state,
				showEditAddressModal: payload
			};

		case 'SET_ACTIVE_STEP':
			return {
				...state,
				activeStep: payload
			};
		case 'SET_CURRENT_SECTION':
			return {
				...state,
				currentSection: payload
			};
		case 'SET_COMPLETED_SECTIONS':
			return {
				...state,
				completedSections: payload
			};
		case 'SET_GOALS_ANSWERS':
			return {
				...state,
				goalsAnswers: payload
			};
		case 'SET_INTAKE_ANSWERS':
			return {
				...state,
				intakeAnswers: payload
			};

		case 'SET_WEIGHT_LOSS_ANSWERS':
			return {
				...state,
				weightLossAnswers: payload
			};
		case 'SET_CURRENTLY_PREGNANT':
			return {
				...state,
				currentlyPregnant: payload
			};
		case 'SET_CUTOFF_WEIGHT':
			return {
				...state,
				cutoffWeight: payload
			};
		case 'SET_CURRENTLY_TAKING_GLP1':
			return {
				...state,
				currentlyTakingGLP1: payload
			};
		case 'SET_EXPERIENCES_SIDE_EFFECTS':
			return {
				...state,
				experiencesSideEffects: payload
			};
		case 'SET_WANTS_ALTERNATIVE_DOSING':
			return {
				...state,
				wantsAlternativeDosing: payload
			};
		case 'SET_AVOID_SIDE_EFFECTS':
			return {
				...state,
				avoidSideEffects: payload
			};

		case 'SET_INTAKE_STEPS':
			return {
				...state,
				intakeSteps: payload
			};
		case 'SET_ACTIVE_INTAKE_STEP':
			return {
				...state,
				activeIntakeStep: payload
			};

		case 'SET_GOALS_STEPS':
			return {
				...state,
				goalsSteps: payload
			};
		case 'SET_ACTIVE_GOALS_STEP':
			return {
				...state,
				activeGoalsStep: payload
			};

		case 'SET_SHOW_SIGN_IN_MODAL':
			return {
				...state,
				showSignInModal: payload
			};
		case 'SET_SHOW_MORE_INFO_MODAL':
			return {
				...state,
				showMoreInfoModal: payload
			};
		case 'SET_SHOW_WEIGHT_LOSS_DISCLAIMER_MODAL':
			return {
				...state,
				showWeightLossDisclaimerModal: payload
			};

		case 'SET_PRODUCT_SKU':
			return {
				...state,
				productSku: payload
			};
		case 'SET_SELECTED_PRODUCT':
			return {
				...state,
				selectedProduct: payload
			};
		case 'SET_SELECTED_FREQUENCY':
			return {
				...state,
				selectedFrequency: payload
			};
		case 'SET_AVAILABLE_PRODUCT_SKUS':
			return {
				...state,
				availableProductSkus: payload
			};
		case 'SET_UNENROLLED_PRODUCT_SKUS':
			return {
				...state,
				unenrolledProductSkus: payload
			};
		case 'SET_SELECTED_VARIANT_SKU':
			return {
				...state,
				selectedVariantSku: payload
			};
		case 'SET_STARTING_LEVEL':
			return {
				...state,
				startingLevel: payload
			};

		case 'SET_FUNNEL_SUBSCRIPTION':
			return {
				...state,
				subscription: payload
			};
		case 'SET_FUNNEL_ENCOUNTER':
			return {
				...state,
				encounter: payload
			};
		case 'SET_FUNNEL_ORDER':
			return {
				...state,
				order: payload
			};

		case 'SET_USER_COUPON_CODE':
			return {
				...state,
				userCouponCode: payload
			};
		case 'SET_SHOW_PAYMENT_SCHEDULE_MODAL':
			return {
				...state,
				showPaymentScheduleModal: payload
			};
		case 'SET_BILLING_SAME_AS_SHIPPING':
			return {
				...state,
				billingSameAsShipping: payload
			};
		case 'SET_PAYMENT_METHODS':
			return {
				...state,
				paymentMethods: payload
			};
		case 'SET_PAYMENT_METHOD_TYPE':
			return {
				...state,
				paymentMethodType: payload
			};
		case 'SET_SHOW_KLARNA_PAYMENT_METHOD':
			return {
				...state,
				showKlarnaPaymentMethod: payload
			};

		case 'SET_IS_FUNNEL_LOADING':
			return {
				...state,
				isFunnelLoading: payload
			};
		case 'SET_IS_FUNNEL_LOADING_MESSAGE':
			return {
				...state,
				isFunnelLoadingMessage: payload
			};
		case 'SET_IS_ACCOUNT_LOADING':
			return {
				...state,
				isAccountLoading: payload
			};
		case 'SET_IS_ACCOUNT_LOADING_MESSAGE':
			return {
				...state,
				isAccountLoadingMessage: payload
			};
		case 'SET_IS_ELIGIBILITY_LOADING':
			return {
				...state,
				isEligibilityLoading: payload
			};
		case 'SET_IS_ELIGIBILITY_LOADING_MESSAGE':
			return {
				...state,
				isEligibilityLoadingMessage: payload
			};
		case 'SET_IS_ADDRESS_LOADING':
			return {
				...state,
				isAddressLoading: payload
			};
		case 'SET_ADDRESS_ERROR':
			return {
				...state,
				addressError: payload
			};
		case 'SET_FUNNEL_ERROR':
			return {
				...state,
				funnelError: payload
			};

		case 'SET_FUNNEL_PROGRAM_SLUG':
			return {
				...state,
				programSlug: payload
			};
		case 'SET_FUNNEL_ADDON':
			return {
				...state,
				addon: payload
			};
		case 'SET_FUNNEL_VERSION':
			return {
				...state,
				version: payload
			};

		case 'RESET_FUNNEL':
			return {
				...state,
				...initialState
			};
		default:
			return state;
	}
};

export const createLead = (profile, programSlug, productSku, firstName, lastName, gender, dateOfBirth, height, weight, BMI, shippingState, phoneVerified) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const analyticsState = { ...state.analyticsState };
			const cartState = { ...state.cartState };
			const funnelState = { ...state.funnelState };
			const orchestrator = { ...state.orchestrator };
			const {
				lead, leadId,
				source, affiliateId, subAffiliateId, campaignId,
				affiliateInfo, deviceInfo, emailInfo, phoneInfo,
				ipAddress, userAgent, referrerUrl,
				testMode, addon, couponCode
			} = analyticsState;
			const { cartItems } = cartState;
			const { goalsAnswers, intakeAnswers, currentlyTakingGLP1, experiencesSideEffects, wantsAlternativeDosing, avoidSideEffects } = funnelState;
			const { surveys, products, variants, programs, productFamilies, offerGroups, funnelChoiceSets } = orchestrator;

			let existingSubscription = null;
			let existingEncounter = null;

			// if the product has a different program than what is being passed in (from siteVisit for example), program for the product takes precedence	
			if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'wl' && isStringNotEmpty(productSku) && !['se', 'segm', 'ti', 'tigm', 'oz', 'ze', 'semaglutide', 'semaglutide-gm', 'tirzepatide', 'tirzepatide-gm', 'ozempic', 'zepbound'].includes(toLower(productSku))) {
				if (['gl', 'gln', 'na', 'nan', 'me', 'glutathione', 'glutathione-n', 'nad-plus', 'nad-plus-n', 'metformin'].includes(toLower(productSku))) {
					programSlug = 'l';
				} else if (['ser', 'sero', 'lb', 'bc', 'sermorelin', 'sermorelin-odt', 'mic-b12', 'b12'].includes(toLower(productSku))) {
					programSlug = 'ms';
				} else if (['fce', 'fct', 'face-cream-e', 'face-cream-r'].includes(toLower(productSku))) {
					programSlug = 'sc';
				} else if (['fhrt'].includes(toLower(productSku))) {
					programSlug = 'fhrt';
				} else if (['semd', 'timd', 'semaglutide-microdosing', 'tirzepatide-microdosing'].includes(toLower(productSku))) {
					programSlug = 'gmd';
				}
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'l' && isStringNotEmpty(productSku) && !['gl', 'gln', 'na', 'nan', 'me', 'glutathione', 'glutathione-n', 'nad-plus', 'nad-plus-n', 'metformin'].includes(toLower(productSku))) {
				if (['se', 'segm', 'ti', 'tigm', 'oz', 'ze', 'semaglutide', 'semaglutide-gm', 'tirzepatide', 'tirzepatide-gm', 'ozempic', 'zepbound'].includes(toLower(productSku))) {
					programSlug = 'wl';
				} else if (['ser', 'sero', 'lb', 'bc', 'sermorelin', 'sermorelin-odt', 'mic-b12', 'b12'].includes(toLower(productSku))) {
					programSlug = 'ms';
				} else if (['fce', 'fct', 'face-cream-e', 'face-cream-r'].includes(toLower(productSku))) {
					programSlug = 'sc';
				} else if (['fhrt'].includes(toLower(productSku))) {
					programSlug = 'fhrt';
				} else if (['semd', 'timd', 'semaglutide-microdosing', 'tirzepatide-microdosing'].includes(toLower(productSku))) {
					programSlug = 'gmd';
				}
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'ms' && isStringNotEmpty(productSku) && !['ser', 'sero', 'lb', 'bc', 'sermorelin', 'sermorelin-odt', 'mic-b12', 'b12'].includes(toLower(productSku))) {
				if (['se', 'segm', 'ti', 'tigm', 'oz', 'ze', 'semaglutide', 'semaglutide-gm', 'tirzepatide', 'tirzepatide-gm', 'ozempic', 'zepbound'].includes(toLower(productSku))) {
					programSlug = 'wl';
				} else if (['gl', 'gln', 'na', 'nan', 'me', 'glutathione', 'glutathione-n', 'nad-plus', 'nad-plus-n', 'metformin'].includes(toLower(productSku))) {
					programSlug = 'l';
				} else if (['fce', 'fct', 'face-cream-e', 'face-cream-r'].includes(toLower(productSku))) {
					programSlug = 'sc';
				} else if (['fhrt'].includes(toLower(productSku))) {
					programSlug = 'fhrt';
				} else if (['semd', 'timd', 'semaglutide-microdosing', 'tirzepatide-microdosing'].includes(toLower(productSku))) {
					programSlug = 'gmd';
				}
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'sc' && isStringNotEmpty(productSku) && !['fce', 'fct', 'face-cream-e', 'face-cream-r'].includes(toLower(productSku))) {
				if (['se', 'segm', 'ti', 'tigm', 'oz', 'ze', 'semaglutide', 'semaglutide-gm', 'tirzepatide', 'tirzepatide-gm', 'ozempic', 'zepbound'].includes(toLower(productSku))) {
					programSlug = 'wl';
				} else if (['gl', 'gln', 'na', 'nan', 'me', 'glutathione', 'glutathione-n', 'nad-plus', 'nad-plus-n', 'metformin'].includes(toLower(productSku))) {
					programSlug = 'l';
				} else if (['ser', 'sero', 'lb', 'bc', 'sermorelin', 'sermorelin-odt', 'mic-b12', 'b12'].includes(toLower(productSku))) {
					programSlug = 'ms';
				} else if (['fhrt'].includes(toLower(productSku))) {
					programSlug = 'fhrt';
				} else if (['semd', 'timd', 'semaglutide-microdosing', 'tirzepatide-microdosing'].includes(toLower(productSku))) {
					programSlug = 'gmd';
				}
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'fhrt' && isStringNotEmpty(productSku) && !['fhrt'].includes(toLower(productSku))) {
				if (['se', 'segm', 'ti', 'tigm', 'oz', 'ze', 'semaglutide', 'semaglutide-gm', 'tirzepatide', 'tirzepatide-gm', 'ozempic', 'zepbound'].includes(toLower(productSku))) {
					programSlug = 'wl';
				} else if (['gl', 'gln', 'na', 'nan', 'me', 'glutathione', 'glutathione-n', 'nad-plus', 'nad-plus-n', 'metformin'].includes(toLower(productSku))) {
					programSlug = 'l';
				} else if (['ser', 'sero', 'lb', 'bc', 'sermorelin', 'sermorelin-odt', 'mic-b12', 'b12'].includes(toLower(productSku))) {
					programSlug = 'ms';
				} else if (['fce', 'fct', 'face-cream-e', 'face-cream-r'].includes(toLower(productSku))) {
					programSlug = 'sc';
				} else if (['semd', 'timd', 'semaglutide-microdosing', 'tirzepatide-microdosing'].includes(toLower(productSku))) {
					programSlug = 'gmd';
				}
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'gmd' && isStringNotEmpty(productSku) && !['semd', 'timd', 'semaglutide-microdosing', 'tirzepatide-microdosing'].includes(toLower(productSku))) {
				if (['se', 'segm', 'ti', 'tigm', 'oz', 'ze', 'semaglutide', 'semaglutide-gm', 'tirzepatide', 'tirzepatide-gm', 'ozempic', 'zepbound'].includes(toLower(productSku))) {
					programSlug = 'wl';
				} else if (['gl', 'gln', 'na', 'nan', 'me', 'glutathione', 'glutathione-n', 'nad-plus', 'nad-plus-n', 'metformin'].includes(toLower(productSku))) {
					programSlug = 'l';
				} else if (['ser', 'sero', 'lb', 'bc', 'sermorelin', 'sermorelin-odt', 'mic-b12', 'b12'].includes(toLower(productSku))) {
					programSlug = 'ms';
				} else if (['fce', 'fct', 'face-cream-e', 'face-cream-r'].includes(toLower(productSku))) {
					programSlug = 'sc';
				} else if (['fhrt'].includes(toLower(productSku))) {
					programSlug = 'fhrt';
				}
			}

			// console.log('programs and programSlug', programSlug, programs);
			let program = getProgramBySlug(programSlug, programs);
			let programId = program?.id ?? 'none';
			const resolvedContext = resolveOfferContext({
				requestedProgramSlug: programSlug,
				requestedProductValue: productSku,
				products,
				programs,
				offerGroups
			});
			if (isObjectNotEmpty(resolvedContext.program) && isStringNotEmpty(resolvedContext.program.slug)) {
				programSlug = resolvedContext.program.slug;
				program = resolvedContext.program;
				programId = program?.id ?? 'none';
			}
			const choiceSet = getFunnelChoiceSetForEntry({
				programId,
				program,
				offerGroupId: resolvedContext.offerGroupId,
				choiceSets: funnelChoiceSets
			});
			const canonicalChoiceProduct = getCanonicalProductForChoiceSetOfferGroup({
				choiceSet,
				offerGroupId: resolvedContext.offerGroupId ?? choiceSet?.defaultOfferGroupId,
				requestedProductValue: productSku,
				program,
				products,
				shippingState,
				testMode
			});

			let product = canonicalChoiceProduct ?? resolvedContext.product ?? null;
			if (!isObjectNotEmpty(product) && isStringNotEmpty(productSku)) {
				product = getProductBySku(productSku, products);
			} else if (!isObjectNotEmpty(product) && toLower(programSlug) === 'wl') {
				product = getProductBySku('SEMAGLUTIDE', products);
			} else if (!isObjectNotEmpty(product) && toLower(programSlug) === 'gmd') {
				product = getProductBySku('SEMAGLUTIDE-MICRODOSING', products);
			} else if (!isObjectNotEmpty(product) && toLower(programSlug) === 'fhrt') {
				product = getProductBySku('FHRT', products);
			}
			if (isObjectNotEmpty(product) && isStringNotEmpty(product.sku)) {
				productSku = product.sku;
			}

			const effectiveProductFamilyId = getEffectiveProductFamilyId(product);
			const effectiveOfferGroupId = resolvedContext.offerGroupId ?? getEffectiveOfferGroupId(product);
			const effectiveOfferToken = resolvedContext.offerToken ?? getEffectiveOfferToken(product, offerGroups);

			let goalsSurvey = null;
			if (isListNotEmpty(surveys) && isStringNotEmpty(programSlug)) {
				goalsSurvey = getSurveyByEffectiveSurveyFlow({ program, product, productFamilies, surveys, flowKey: SURVEY_FLOW_KEYS.GOALS });
				if (!isObjectNotEmpty(goalsSurvey)) {
					if (toLower(programSlug) === 'wl') {
						goalsSurvey = getSurveyBySlug('weight-loss-goals2', surveys);
					} else if (toLower(programSlug) === 'l') {
						goalsSurvey = getSurveyBySlug('longevity-goals', surveys);
					} else if (toLower(programSlug) === 'ms') {
						goalsSurvey = getSurveyBySlug('metabolic-support-goals', surveys);
					} else if (toLower(programSlug) === 'fhrt') {
						goalsSurvey = getSurveyBySlug('fhrt-goals', surveys);
					} else if (toLower(programSlug) === 'sc') {
						goalsSurvey = getSurveyBySlug('sc-goals', surveys);
					} else if (toLower(programSlug) === 'gmd') {
						goalsSurvey = getSurveyBySlug('gmd-goals', surveys);
					}
				}
			}

			let intakeSurvey = null;
			if (isListNotEmpty(surveys) && isStringNotEmpty(programSlug)) {
				intakeSurvey = getSurveyByEffectiveSurveyFlow({ program, product, productFamilies, surveys, flowKey: SURVEY_FLOW_KEYS.INTAKE });
				if (!isObjectNotEmpty(intakeSurvey)) {
					if (toLower(programSlug) === 'wl') {
						intakeSurvey = getSurveyBySlug('weight-loss-qualifying-form', surveys);
					} else if (toLower(programSlug) === 'l') {
						intakeSurvey = getSurveyBySlug('longevity-form4', surveys);
					} else if (toLower(programSlug) === 'ms') {
						if (toLower(productSku) === 'mic-b12' || toLower(productSku) === 'b12') {
							intakeSurvey = getSurveyBySlug('metabolic-support-form-mic-b12', surveys);
						} else {
							intakeSurvey = getSurveyBySlug('metabolic-support-form2', surveys);
						}
					} else if (toLower(programSlug) === 'fhrt') {
						intakeSurvey = getSurveyBySlug('fhrt-intake', surveys);
					} else if (toLower(programSlug) === 'sc') {
						intakeSurvey = getSurveyBySlug('sc-intake', surveys);
					} else if (toLower(programSlug) === 'gmd') {
						intakeSurvey = getSurveyBySlug('gmd-intake', surveys);
					}
				}
			}

			let availableProducts = [];
			if (isStringNotEmpty(programSlug) && isListNotEmpty(products) && isListNotEmpty(programs)) {
				const programProducts = getProductsByProgramSlug(programSlug, products, programs);
				availableProducts = programProducts.filter(i => isProductEligibleForFunnel(i, { shippingState, testMode }));
			}

			// console.log('createLead called', leadId, lead, programId, productSku, profile?.userId, lead?.status);
			let newAffiliateInfo = affiliateInfo;
			let newLead = lead;
			if (isStringNotEmpty(programId) && programId !== "none" && isObjectNotEmpty(profile) && isStringNotEmpty(profile?.userId)) {
				if (['l', 'ms', 'gmd', 'fhrt', 'sc'].includes(toLower(programSlug)) && isStringNotEmpty(productSku)) {
					if (isStringEmpty(leadId)) {
						let list = await Lead.search({ userId: profile?.userId, status: ['CREATED', 'SUBMITTED_WITH_ERROR'], programId: programId, productSku: productSku });
						// console.log('found existing leads', list);
						if (isListNotEmpty(list)) {
							let orderedList = orderBy(list, ['modifiedAt'], ['desc']);
							let latestLead = orderedList[0];
							if (isObjectNotEmpty(latestLead) && isStringNotEmpty(latestLead.id)) {
								newLead = { ...latestLead, bmi: BMI, height: height, weight: weight };
							} else {
								newLead = null;
							}
						} else {
							newLead = null;
						}
					} else if (isStringNotEmpty(leadId)) {
						let match = await Lead.getById(leadId);
						// make sure user owns the lead record
						if (isObjectNotEmpty(match)) {
							// deliberate: attribution carries forward when this lead turns out not to be
							// resumable and a fresh one is created below. a DISQUALIFIED lead fails the status
							// clause, and the customer who comes back through the funnel was still won by the
							// affiliate who sent them - dropping it here would credit that conversion to no one
							newAffiliateInfo = match.affiliateInfo;
							if (isStringNotEmpty(match?.id) && match?.userId === profile?.userId && match?.programId === programId && match?.productSku === productSku && ['CREATED', 'SUBMITTED_WITH_ERROR'].includes(match?.status)) {
								newLead = { ...match, bmi: BMI, height: height, weight: weight };
							} else {
								if (match.programId === 'none') {
									// lead is bad so mark as a duplicate to clear it out
									let updatedLeadFields = {
										status: 'DUPLICATE',
										statusHistory: StatusHistory.addNewStatus('DUPLICATE', match?.statusHistory)
									};
									const marked = await markLeadDuplicate(match?.id, updatedLeadFields);
									if (isBooleanTrue(marked)) {
										logger.logDebugEvent('marked lead as duplicate', match?.id, false);
									}

									newLead = null;
								} else {
									let list = await Lead.search({ userId: profile?.userId, status: ['CREATED', 'SUBMITTED_WITH_ERROR'], programId: programId, productSku: productSku });
									// console.log('found existing leads', list);
									if (isListNotEmpty(list)) {
										let orderedList = orderBy(list, ['modifiedAt'], ['desc']);
										let latestLead = orderedList[0];
										if (isObjectNotEmpty(latestLead) && isStringNotEmpty(latestLead.id)) {
											newLead = { ...latestLead, bmi: BMI, height: height, weight: weight };
										} else {
											newLead = null;
										}
									} else {
										newLead = null;
									}
								}
							}
						} else {
							newLead = null;
						}
					} else if (isObjectNotEmpty(lead)) {
						if (isStringNotEmpty(lead?.id) && lead?.userId === profile?.userId && lead?.programId === programId && lead?.productSku === productSku && ['CREATED', 'SUBMITTED_WITH_ERROR'].includes(lead?.status)) {
							newLead = { ...lead, bmi: BMI, height: height, weight: weight };
						} else {
							newLead = null;
						}
					} else {
						newLead = null;
					}
				} else if (toLower(programSlug) === 'wl') {
					if (isStringEmpty(leadId)) {
						let list = await Lead.search({ userId: profile?.userId, status: ['CREATED', 'SUBMITTED_WITH_ERROR'], programId: programId });
						// console.log('found existing leads', list);
						if (isListNotEmpty(list)) {
							let orderedList = orderBy(list, ['modifiedAt'], ['desc']);
							let latestLead = orderedList[0];
							if (isObjectNotEmpty(latestLead) && isStringNotEmpty(latestLead.id)) {
								newLead = { ...latestLead, bmi: BMI, height: height, weight: weight };
							} else {
								newLead = null;
							}
						} else {
							newLead = null;
						}
					} else if (isStringNotEmpty(leadId)) {
						let match = await Lead.getById(leadId);
						// make sure user owns the lead record
						// console.log('MATCH', match);
						if (isObjectNotEmpty(match)) {
							if (isStringNotEmpty(match?.id) && match?.userId === profile?.userId && match?.programId === programId && ['CREATED', 'SUBMITTED_WITH_ERROR'].includes(match?.status)) {
								newLead = { ...match, bmi: BMI, height: height, weight: weight };
							} else {
								if (match.programId === 'none') {
									// lead is bad so mark as a duplicate to clear it out
									let updatedLeadFields = {
										status: 'DUPLICATE',
										statusHistory: StatusHistory.addNewStatus('DUPLICATE', match?.statusHistory)
									};
									const marked = await markLeadDuplicate(match?.id, updatedLeadFields);
									if (isBooleanTrue(marked)) {
										logger.logDebugEvent('marked lead as duplicate', match?.id, false);
									}

									newLead = null;
								} else {
									let list = await Lead.search({ userId: profile?.userId, status: ['CREATED', 'SUBMITTED_WITH_ERROR'], programId: programId });
									// console.log('found existing leads', list);
									if (isListNotEmpty(list)) {
										let orderedList = orderBy(list, ['modifiedAt'], ['desc']);
										let latestLead = orderedList[0];
										if (isObjectNotEmpty(latestLead) && isStringNotEmpty(latestLead.id)) {
											newLead = { ...latestLead, bmi: BMI, height: height, weight: weight };
										} else {
											newLead = null;
										}
									} else {
										newLead = null;
									}
								}
							}
						} else {
							newLead = null;
						}
					} else if (isObjectNotEmpty(lead)) {
						if (isStringNotEmpty(lead?.id) && lead?.userId === profile?.userId && lead?.programId === programId && ['CREATED', 'SUBMITTED_WITH_ERROR'].includes(lead?.status)) {
							newLead = { ...lead, bmi: BMI, height: height, weight: weight };
						} else {
							newLead = null;
						}
					} else {
						newLead = null;
					}
				}
			}

			if (isObjectNotEmpty(newLead) && isObjectNotEmpty(profile) && isStringNotEmpty(profile?.userId)) {
				await dispatch(setLeadId(newLead?.id));

				// only send the biometrics that actually changed - funnel initialization backfills these values
				// from the profile itself, so an unconditional call would echo the profile back to itself on
				// every funnel visit. the server dedups a re-sent value too, but a page load is not a reading
				// and should not cost a callable
				const changedBiometrics = {};
				if (isNotNullOrUndefined(BMI) && String(BMI) !== String(profile?.bmi)) { changedBiometrics.bmi = BMI; }
				if (isNotNullOrUndefined(height) && String(height) !== String(profile?.height)) { changedBiometrics.height = height; }
				const isWeightChanged = isNotNullOrUndefined(weight) && String(weight) !== String(profile?.weight);
				if (isWeightChanged || isObjectNotEmpty(changedBiometrics)) {
					await dispatch(recordProfileWeight(profile?.userId, isWeightChanged ? weight : null, { ...changedBiometrics, source: 'FUNNEL', sourceEntityId: newLead?.id }));
				}
				await dispatch(updateLead(newLead?.id, { bmi: BMI, height: height, weight: weight }));

				let orderedLeadForms = orderBy(isListNotEmpty(newLead?.forms) ? [...newLead.forms] : [], ['createdAt'], ['desc']);

				if (isObjectNotEmpty(goalsSurvey)) {
					let goalsFormObj = orderedLeadForms.find(i => i.surveyId === goalsSurvey?.id);
					if (isObjectNotEmpty(goalsFormObj) && isListEmpty(goalsAnswers)) {
						let currentGoalsAnswers = isListNotEmpty(goalsFormObj.answers) ? [...goalsFormObj.answers] : []
						if (isNumberNotEmpty(BMI)) {
							currentGoalsAnswers = updateRecord('name', { name: 'BMI', customName: 'BMI', label: 'What is your BMI?', value: BMI.toFixed(1) }, [...currentGoalsAnswers]);
						}
						if (isObjectEmpty(goalsFormObj?.answers?.find(i => i.name === 'currentlyTakingGLP1')) && isStringNotEmpty(currentlyTakingGLP1) && toLower(programSlug) === 'wl') {
							currentGoalsAnswers = updateRecord('name', { name: 'currentlyTakingGLP1', customName: 'currentlyTakingGLP1', label: 'Are you currently taking GLP-1 Medication?', value: currentlyTakingGLP1 }, [...currentGoalsAnswers]);
						} else {
							await dispatch(setCurrentlyTakingGLP1(goalsFormObj?.answers?.find(i => i.name === 'currentlyTakingGLP1')?.value));
						}

						await dispatch(setGoalsAnswers(currentGoalsAnswers));
					}

					if (isObjectNotEmpty(goalsFormObj?.answers?.find(i => i.name === 'currentlyTakingGLP1')) && isStringNotEmpty(goalsFormObj?.answers?.find(i => i.name === 'currentlyTakingGLP1')?.value)) {
						await dispatch(setCurrentlyTakingGLP1(goalsFormObj?.answers?.find(i => i.name === 'currentlyTakingGLP1')?.value));
					}
				}

				if (isObjectNotEmpty(intakeSurvey)) {
					let intakeFormObj = orderedLeadForms.find(i => i.surveyId === intakeSurvey?.id);
					if (isObjectNotEmpty(intakeFormObj) && isListEmpty(intakeAnswers)) {
						let currentIntakeAnswers = isListNotEmpty(intakeFormObj.answers) ? [...intakeFormObj.answers] : []
						if (isStringNotEmpty(gender)) {
							currentIntakeAnswers = updateRecord('name', { name: 'gender', customName: 'gender', label: 'What is your gender?', value: gender }, [...currentIntakeAnswers]);
						}
						if (isObjectEmpty(intakeFormObj?.answers?.find(i => i.customName === 'sensitivityToMedications')) && isStringNotEmpty(experiencesSideEffects) && toLower(programSlug) === 'wl') {
							currentIntakeAnswers = updateRecord('name', { name: 'radiobuttons_B421E28F-A405-4FC6-9651-27FC4AF59D3A', customName: 'sensitivityToMedications', label: 'People have different sensitivities to medications and commonly experience side effects to standard dosing regimens. Do you commonly experience side effects to medications?', value: experiencesSideEffects }, [...currentIntakeAnswers]);
						} else {
							await dispatch(setExperiencesSideEffects(intakeFormObj?.answers?.find(i => i.customName === 'sensitivityToMedications')?.value));
						}
						if (isObjectEmpty(intakeFormObj?.answers?.find(i => i.customName === 'showCompounded')) && isStringNotEmpty(wantsAlternativeDosing) && toLower(programSlug) === 'wl') {
							currentIntakeAnswers = updateRecord('name', { name: 'radiobuttons_A88C0E76-5F71-4684-B3B4-48202B555D1D', customName: 'showCompounded', label: 'How important is the ability to tailor your prescription experience to your individual preferences or needs?', value: wantsAlternativeDosing }, [...currentIntakeAnswers]);
						} else {
							await dispatch(setWantsAlternativeDosing(intakeFormObj?.answers?.find(i => i.customName === 'showCompounded')?.value));
						}
						if (isObjectEmpty(intakeFormObj?.answers?.find(i => i.customName === 'avoidSideEffects')) && isStringNotEmpty(avoidSideEffects) && toLower(programSlug) === 'wl') {
							currentIntakeAnswers = updateRecord('name', { name: 'radiobuttons_B493AAC1-590A-4BDE-9009-06501CD73CB0', customName: 'avoidSideEffects', label: 'Would nausea, vomiting, or diarrhea disrupt your daily routine?', value: avoidSideEffects }, [...currentIntakeAnswers]);
						} else {
							await dispatch(setAvoidSideEffects(intakeFormObj?.answers?.find(i => i.customName === 'avoidSideEffects')?.value));
						}

						await dispatch(setIntakeAnswers(currentIntakeAnswers));
					}

					if (isObjectNotEmpty(intakeFormObj?.answers?.find(i => i.customName === 'sensitivityToMedications')) && isStringNotEmpty(intakeFormObj?.answers?.find(i => i.customName === 'sensitivityToMedications')?.value)) {
						await dispatch(setExperiencesSideEffects(intakeFormObj?.answers?.find(i => i.customName === 'sensitivityToMedications')?.value));
					}

					if (isObjectNotEmpty(intakeFormObj?.answers?.find(i => i.customName === 'showCompounded')) && isStringNotEmpty(intakeFormObj?.answers?.find(i => i.customName === 'showCompounded')?.value)) {
						await dispatch(setWantsAlternativeDosing(intakeFormObj?.answers?.find(i => i.customName === 'showCompounded')?.value));
					}

					if (isObjectNotEmpty(intakeFormObj?.answers?.find(i => i.customName === 'avoidSideEffects')) && isStringNotEmpty(intakeFormObj?.answers?.find(i => i.customName === 'avoidSideEffects')?.value)) {
						await dispatch(setAvoidSideEffects(intakeFormObj?.answers?.find(i => i.customName === 'avoidSideEffects')?.value));
					}
				}

				if (['l', 'ms', 'sc', 'fhrt'].includes(toLower(programSlug))) {
					if (isListNotEmpty(newLead?.cartItems)) {
						let defaultCartItem = newLead?.cartItems?.find(i => i.sku === productSku);
						if (isObjectNotEmpty(defaultCartItem) && isStringNotEmpty(defaultCartItem?.id) && isStringNotEmpty(defaultCartItem?.variantId)) {
							// console.log('loadCartFromLead', newLead?.cartItems);
							// console.log('defaultCartItem', defaultCartItem, defaultCartItem?.id, defaultCartItem?.variantId);
							// check that variant is still enabled
							if (isObjectNotEmpty(product) && isListNotEmpty(variants) && isObjectNotEmpty(variants?.find(i => i.productId === product?.id && isBooleanTrue(i.enabled) && i.id === defaultCartItem?.variantId))) {
								let variantForCart = variants?.find(i => i.productId === product?.id && isBooleanTrue(i.enabled) && i.id === defaultCartItem?.variantId);
								await dispatch(setSelectedVariantSku(variantForCart?.sku));
								// the CART carries the count that is actually charged, so it is clamped with the
								// selected frequency rather than after it - a radio reading Monthly over a cart
								// still holding a withdrawn 3-month bundle is worse than no radio matching at all
								await dispatch(setSelectedFrequency(toSelectableBundleCount(defaultCartItem?.bundleCount, product)));
								await dispatch(loadCartFromLead(newLead?.cartItems.map(i => {
									return new CartItem({
										...i,
										id: product?.id,
										sku: product?.sku,
										variantId: variantForCart?.id,
										variantSku: variantForCart?.sku,
										bundleCount: toSelectableBundleCount(i?.bundleCount, product)
									}).toDto();
								})));
							}
						}
					}
				} else if (['wl', 'gmd'].includes(toLower(programSlug))) {
					if (isListNotEmpty(newLead?.cartItems)) {
						let defaultCartItem = getLeadCartItemForResolvedProduct({ cartItems: newLead?.cartItems, product, productSku, effectiveOfferGroupId, products });
						if (isObjectNotEmpty(defaultCartItem) && isObjectNotEmpty(product) && isStringNotEmpty(product?.id)) {
							let variantForCart = getVariantForLeadCartItem(defaultCartItem, product, variants);
							if (isObjectNotEmpty(variantForCart)) {
								const bundleCount = toSelectableBundleCount(defaultCartItem?.bundleCount, product);
								await dispatch(setProductSku(product?.sku));
								await dispatch(setSelectedProduct(product));
								await dispatch(setSelectedVariantSku(variantForCart?.sku));
								await dispatch(setSelectedFrequency(bundleCount));
								await dispatch(loadCartFromLead([new CartItem({
									...defaultCartItem,
									id: product?.id,
									sku: product?.sku,
									variantId: variantForCart?.id,
									variantSku: variantForCart?.sku,
									quantity: isNumberGreaterThanZero(defaultCartItem?.quantity) ? defaultCartItem.quantity : 1,
									bundleCount
								}).toDto()]));
							}
						}
					}
				}
			}

			// check to see if customer is already subscribed before creating a new lead
			// console.log('checking for duplicate enrollments, existing lead: ', newLead);
			let existingSubscriptions = [];
			const duplicateProgramIds = getDuplicateProgramIds(program, programs);
			if (isStringNotEmpty(profile?.userId) && isListNotEmpty(duplicateProgramIds)) {
				existingSubscriptions = await Subscription.getByUserIdAndProgramIdAndStatus(profile?.userId, duplicateProgramIds.length === 1 ? duplicateProgramIds[0] : duplicateProgramIds, DUPLICATE_BLOCKING_STATUSES);
				existingSubscription = findBlockingEntityForDuplicateRule({
					targetProgram: program,
					targetProduct: product,
					entities: existingSubscriptions,
					products,
					programs,
					productFamilies
				});
			}

			if (isObjectNotEmpty(existingSubscription) && isStringNotEmpty(existingSubscription?.id)) {
				await dispatch(setSubscription(existingSubscription));
				// console.log('existing subscription', existingSubscription);

				let existingEncounters = await Encounter.getBySubscriptionIdAndUserId(existingSubscription?.id, profile?.userId);
				// console.log('existing encounters', existingEncounters);
				if (isListNotEmpty(existingEncounters)) {
					existingEncounter = existingEncounters[0];
					await dispatch(setEncounter(existingEncounter));
				} else {
					await dispatch(setEncounter(null));
					reportMissingSubscriptionEncounterIssue(existingSubscription.id, {
						source: 'FUNNEL_DUPLICATE_CHECK',
						programId,
						productSku,
						leadId: newLead?.id ?? null,
						pathName: typeof window !== 'undefined' ? window.location?.pathname ?? null : null,
						url: typeof window !== 'undefined' ? window.location?.href ?? null : null
					}).catch((err) => {
						logger.logErrorEvent('REPORT_MISSING_SUBSCRIPTION_ENCOUNTER_ISSUE', err, getErrorMessage(err), false);
					});
				}

				// if existing lead is tied to a subscription and encounter, clear them out of the session
				// otherwise, mark the lead as a duplicate

				// console.log('existing lead to mark as duplicate', newLead);
				if (isObjectNotEmpty(newLead) && isStringNotEmpty(newLead?.id) && newLead?.status !== 'DUPLICATE' && isListEmpty(newLead?.encounterIds) && isListEmpty(newLead?.subscriptionIds)) {
					// lead is a duplicate so mark it as such
					let updatedLeadFields = {
						status: 'DUPLICATE',
						statusHistory: StatusHistory.addNewStatus('DUPLICATE', newLead?.statusHistory)
					};
					const marked = await markLeadDuplicate(newLead?.id, updatedLeadFields);
					if (isBooleanTrue(marked)) {
						logger.logDebugEvent('marked lead as duplicate', newLead?.id, false);
					}
				}

				await dispatch(setLeadId(null));
				await dispatch(setLead(null));
				newLead = null;
				// console.log('existing duplicate subscription', existingSubscription);
				// console.log('DONT CREATE A NEW LEAD');
			} else {
				await dispatch(setSubscription(null));
				await dispatch(setEncounter(null));

				// console.log('createLead called 2', newLead?.id, newLead, programId, productSku, product, profile?.userId, newLead?.status);
				if (isObjectEmpty(newLead) && isObjectNotEmpty(profile) && isStringNotEmpty(profile?.userId) && isStringNotEmpty(programId) && programId !== 'none' && isObjectNotEmpty(product) && isStringNotEmpty(product?.sku)) {
					// console.log('CREATE A NEW LEAD');

					let shippingStateVal = isStringNotEmpty(shippingState) ? shippingState : (isStringNotEmpty(profile?.shippingAddress?.state) ? profile?.shippingAddress?.state : null);
					let stateProductRule = product?.productRules.find(i => i.state === shippingStateVal);

					newLead = await new Lead({
						userId: profile?.userId,

						status: 'CREATED',

						email: profile?.email,
						phone: profile?.phone,

						firstName: profile?.firstName ?? firstName,
						middleName: profile?.middleName ?? null,
						lastName: profile?.lastName ?? lastName,
						suffix: profile?.suffix ?? null,

						gender: profile?.gender ?? gender,
						dateOfBirth: profile?.dateOfBirth ?? dateOfBirth,

						height: profile?.height ?? height,
						weight: profile?.weight ?? weight,
						bmi: profile?.bmi ?? BMI,

						shippingAddress: profile?.shippingAddress,
						billingAddress: profile?.billingAddress,

						source: source,
						affiliateId: newAffiliateInfo?.affiliateId ?? affiliateId,
						subAffiliateId: newAffiliateInfo?.subAffiliateId ?? subAffiliateId,
						campaignId: campaignId,
						programId: programId,
						productSku: product?.sku ?? null,
						productFamilyId: effectiveProductFamilyId,
						offerGroupId: effectiveOfferGroupId,
						offerToken: effectiveOfferToken,
						commercialEquivalencyGroupId: product?.commercialEquivalencyGroupId ?? null,

						affiliateInfo: newAffiliateInfo,
						deviceInfo: deviceInfo,
						emailInfo: emailInfo,
						phoneInfo: phoneInfo,

						ipAddress: ipAddress,
						userAgent: userAgent,
						referrerUrl: referrerUrl,

						originalSubmission: {
							...profile,
							goalsAnswers: goalsAnswers,
							shippingState: shippingStateVal,
							currentlyTakingGLP1: currentlyTakingGLP1
						},

						phoneVerified: phoneVerified,

						acceptAgreements: true,

						coveredEntityId: product?.practiceId ?? null,
						pharmacyId: product?.pharmacyId ?? null,

						// default to the state product rule first
						requiresConfirmation: stateProductRule?.requiresConfirmation ?? product?.requiresConfirmation,
						requiresClinicianApproval: stateProductRule?.requiresClinicianApproval ?? product?.requiresClinicianApproval,
						requiresClinicianEncounter: stateProductRule?.requiresClinicianEncounter ?? product?.requiresClinicianEncounter,
						requiresSurveys: stateProductRule?.requiresSurveys ?? product?.requiresSurveys,

						requiredDocuments: stateProductRule?.requiredDocuments ?? product?.requiredDocuments ?? [],

						shippingState: shippingStateVal,

						cartItems: cartItems.map((cartItem) => new CartItem(cartItem).toDto()),

						testMode: testMode,

						addon: addon,

						couponCode: couponCode
					}).create();
					// console.log('NEW LEAD CREATED', newLead);
					if (isObjectNotEmpty(newLead) && isStringNotEmpty(newLead?.id)) {
						// if (isStringNotEmpty(newLead?.affiliateId) && newLead?.affiliateId !== 'none') {
						// 	await sendFirstPromoterSignUp(newLead?.userId, newLead?.affiliateId, ipAddress, testMode);
						// }

						await dispatch(setLeadId(newLead?.id));
						await dispatch(setLead(newLead));
					} else {
						await dispatch(setLeadId(null));
						await dispatch(setLead(null));
					}
				} else {
					// console.log('LEAD ALREADY EXISTS', newLead);
				}
			}

			return { lead: newLead, subscription: existingSubscription, encounter: existingEncounter };
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		return { lead: null, subscription: null, encounter: null };
	}
};

export const initiateFunnel = (programSlug, productSkuVal, version) => {
	return async (dispatch, getState) => {
		await dispatch(setIsFunnelLoading(true));
		console.log('Initiating funnel...');
		if (isStringEmpty(productSkuVal) && isStringNotEmpty(programSlug) && ['l', 'ms'].includes(toLower(programSlug))) {
			const state5 = getState();
			const analyticsState = { ...state5.analyticsState };
			const { leadId } = analyticsState;
			if (isStringNotEmpty(leadId)) {
				let existingLeadForProduct = await Lead.getById(leadId);
				console.log('existingLeadForProduct', existingLeadForProduct);
				console.log('existingLeadForProduct productSku', existingLeadForProduct?.productSku);
				productSkuVal = existingLeadForProduct?.offerToken ?? existingLeadForProduct?.productSku ?? null;
			}
		}

		let funnelName = 'INTAKE';

		try {
			let programs = await dispatch(getPrograms(false));
			let offerGroups = await dispatch(getOfferGroups(false));
			let funnelChoiceSets = await dispatch(getFunnelChoiceSets(false));
			let products = await dispatch(getProducts(false));
			let variants = await dispatch(getVariants(false));
			let surveys = await dispatch(getSurveys(false));
			let productFamilies = await dispatch(getProductFamilies(false));
			const resolvedContext = resolveOfferContext({
				requestedProgramSlug: programSlug,
				requestedProductValue: productSkuVal,
				products,
				programs,
				offerGroups
			});

			if (isObjectNotEmpty(resolvedContext.program) && isStringNotEmpty(resolvedContext.program.slug)) {
				programSlug = resolvedContext.program.slug;
			}

			const state = getState();
			const authState = { ...state.authState };
			const analyticsState = { ...state.analyticsState };
			const cartState = { ...state.cartState };
			const orchestrator = { ...state.orchestrator };
			const { profile, firstName, lastName, height, weight, BMI, dateOfBirth, gender, shippingState, phoneVerified } = authState;
			const { lead, leadId } = analyticsState;
			const { cartItems } = cartState;
			const { programs: loadedPrograms, funnelChoiceSets: loadedFunnelChoiceSets } = orchestrator;
			programs = isListNotEmpty(loadedPrograms) ? loadedPrograms : programs;
			funnelChoiceSets = isListNotEmpty(loadedFunnelChoiceSets) ? loadedFunnelChoiceSets : funnelChoiceSets;

			if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'wl' && isStringNotEmpty(version)) {
				funnelName = `WL_INTAKE-${version}`;
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'wl' && isStringEmpty(version)) {
				funnelName = `WL_INTAKE-5`;
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'l') {
				funnelName = `L_INTAKE`;
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'ms') {
				funnelName = `MS_INTAKE`;
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'fhrt') {
				funnelName = `FHRT_INTAKE`;
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'sc') {
				funnelName = `SC_INTAKE`;
			} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'gmd') {
				funnelName = `GMD_INTAKE`;
			}

			if (isListNotEmpty(products) && isListNotEmpty(variants) && isListNotEmpty(surveys)) {
				logger.logDebugEvent('initiatingFunnel', {
					programSlug,
					productSkuVal,
					resolvedProductSku: resolvedContext.product?.sku ?? null,
					resolvedOfferToken: resolvedContext.offerToken ?? null,
					version,
					profile
				}, false);

				if (isStringNotEmpty(programSlug)) {
					await dispatch(setProgramSlug(programSlug));
				} else {
					await dispatch(setProgramSlug(null));
				}

				if (isStringNotEmpty(version)) {
					await dispatch(setVersion(version));
				} else {
					await dispatch(setVersion(null));
				}

				const choiceSet = getFunnelChoiceSetForEntry({
					programId: resolvedContext.program?.id ?? null,
					program: resolvedContext.program ?? null,
					offerGroupId: resolvedContext.offerGroupId,
					choiceSets: funnelChoiceSets
				});
				const canonicalChoiceProduct = getCanonicalProductForChoiceSetOfferGroup({
					choiceSet,
					offerGroupId: resolvedContext.offerGroupId ?? choiceSet?.defaultOfferGroupId,
					requestedProductValue: productSkuVal,
					program: resolvedContext.program ?? null,
					products,
					shippingState,
					testMode: analyticsState?.testMode
				});
				if (isObjectNotEmpty(canonicalChoiceProduct)) {
					resolvedContext.product = canonicalChoiceProduct;
				}

				let productSku = resolvedContext.product?.sku ?? productSkuVal;

				if (isStringNotEmpty(productSku)) {
					await dispatch(setProductSku(productSku));
				} else {
					await dispatch(setProductSku(null));
				}

				// setup default product and default variant
				let defaultProduct = isObjectNotEmpty(resolvedContext.product)
					? resolvedContext.product
					: getDefaultProduct(programSlug, productSku, products, programs);
				let defaultVariant = null;
				let defaultBundleCount = 1;
				if (isObjectNotEmpty(defaultProduct)) {
					let productVariants = variants.filter(i => i.productId === defaultProduct?.id);
					let availableVariants = productVariants?.filter(i => isBooleanTrue(i.enabled));
					let orderedVariants = orderBy(availableVariants, ['displayOrder'], ['asc']);
					defaultVariant = new Variant(isListNotEmpty(orderedVariants) ? orderedVariants[0] : null).toDto();
					defaultBundleCount = toMaxBundleCount(defaultProduct, profile);
				}
				if (isObjectNotEmpty(defaultProduct) && isListEmpty(cartItems) && ['wl'].includes(toLower(programSlug))) {
					await dispatch(onChangeProduct(defaultProduct?.sku));
				}
				if (isObjectNotEmpty(defaultProduct) && isObjectNotEmpty(defaultVariant) && isStringNotEmpty(defaultVariant?.sku) && ['l', 'ms', 'gmd', 'fhrt', 'sc'].includes(toLower(programSlug))) {
					await dispatch(setSelectedVariantSku(defaultVariant?.sku));
					await dispatch(setSelectedProduct(defaultProduct));
					await dispatch(setSelectedFrequency(defaultBundleCount));
					await dispatch(addSingleItemToCart(defaultProduct, defaultBundleCount, defaultVariant));
				}

				let newLead = null;
				let existingSubscription = null;
				let existingEncounter = null;
				if (isObjectNotEmpty(profile) && isStringNotEmpty(profile?.userId)) {
					let firstNameVal = firstName;
					if (isStringEmpty(firstName) && isStringNotEmpty(profile?.firstName)) {
						firstNameVal = profile?.firstName;
						await dispatch(setFirstName(firstNameVal));
					}
					let lastNameVal = lastName;
					if (isStringEmpty(lastName) && isStringNotEmpty(profile?.lastName)) {
						lastNameVal = profile?.lastName;
						await dispatch(setLastName(lastNameVal));
					}
					let heightVal = height;
					if (isNumberEmpty(height)) {
						heightVal = profile?.height;
						await dispatch(setHeight(heightVal));
					}
					let weightVal = weight;
					if (isNumberEmpty(weight)) {
						weightVal = profile?.weight;
						await dispatch(setWeight(weightVal));
					}
					let BMIVal = BMI;
					if (isNumberEmpty(BMI) && isNumberGreaterThanZero(heightVal) && isNumberGreaterThanZero(weightVal)) {
						BMIVal = 703 * weightVal / (heightVal * heightVal);
						await dispatch(setBMI(BMIVal));
					}
					let dateOfBirthVal = dateOfBirth;
					if (isStringEmpty(dateOfBirth)) {
						dateOfBirthVal = profile?.dateOfBirth;
						await dispatch(setDateOfBirth(dateOfBirthVal));
					}
					let genderVal = gender;
					if (isStringEmpty(gender)) {
						genderVal = profile?.gender;
						await dispatch(setGender(genderVal));
						if (genderVal === 'MALE') {
							await dispatch(setCurrentlyPregnant(null));
						}
					}
					let shippingStateVal = shippingState;
					if (isStringEmpty(shippingStateVal)) {
						if (isStringNotEmpty(lead?.shippingState)) {
							shippingStateVal = lead?.shippingState;
						} else if (isStringNotEmpty(lead?.shippingAddress?.state)) {
							shippingStateVal = lead?.shippingAddress?.state;
						} else if (isStringNotEmpty(profile?.shippingAddress?.state)) {
							shippingStateVal = profile?.shippingAddress?.state;
						}
						await dispatch(setShippingState(shippingStateVal));
					}

					const { subscription, encounter, lead: updatedLead } = await dispatch(createLead(profile, programSlug, productSku, firstNameVal, lastNameVal, genderVal, dateOfBirthVal, heightVal, weightVal, BMIVal, shippingStateVal, phoneVerified));
					newLead = updatedLead;
					existingEncounter = encounter;
					existingSubscription = subscription ?? null;
				}

				// const state2 = getState();
				// const analyticsState2 = { ...state2.analyticsState };
				// const { siteVisit } = analyticsState2;
				// if (isObjectNotEmpty(siteVisit) && isStringNotEmpty(siteVisit?.id)) {
				// 	let newSiteEvent = await new SiteEvent({
				// 		...siteVisit,
				// 		leadId: newLead?.id ?? null,
				// 		userId: profile?.userId ?? null,

				// 		funnel: funnelName,
				// 		stepNumber: 1,
				// 		stepName: 'INTRO_STEP',
				// 		direction: 'submitted',

				// 		shippingState: newLead?.shippingState ?? null,
				// 		gender: newLead?.gender ?? null,
				// 		dateOfBirth: newLead?.dateOfBirth ?? null,
				// 		bmi: newLead?.BMI ?? 0,

				// 		siteVisitId: siteVisit?.id,
				// 		eventName: `${toLower(programSlug)}_1_f`,
				// 		pathName: window?.location?.pathname,

				// 		testMode: newLead?.testMode ?? false,
				// 		url: window?.location?.href,
				// 		campaignId: newLead?.campaignId ?? 'none',
				// 		programId: newLead?.programId ?? 'none'
				// 	}).create();
				// }

				// console.log('created new lead', newLead);
				if (isObjectNotEmpty(newLead) && isStringNotEmpty(newLead?.id)) {
					await dispatch(setLead(newLead));
					await dispatch(setLeadId(newLead?.id));

					let goalsSurvey = null;
					if (isListNotEmpty(surveys) && isStringNotEmpty(programSlug)) {
						goalsSurvey = getSurveyByEffectiveSurveyFlow({ program: resolvedContext.program, product: defaultProduct, productFamilies, surveys, flowKey: SURVEY_FLOW_KEYS.GOALS });
						if (!isObjectNotEmpty(goalsSurvey)) {
							if (toLower(programSlug) === 'wl') {
								goalsSurvey = getSurveyBySlug('weight-loss-goals2', surveys);
							} else if (toLower(programSlug) === 'l') {
								goalsSurvey = getSurveyBySlug('longevity-goals', surveys);
							} else if (toLower(programSlug) === 'ms') {
								goalsSurvey = getSurveyBySlug('metabolic-support-goals', surveys);
							} else if (toLower(programSlug) === 'fhrt') {
								goalsSurvey = getSurveyBySlug('fhrt-goals', surveys);
							} else if (toLower(programSlug) === 'sc') {
								goalsSurvey = getSurveyBySlug('sc-goals', surveys);
							} else if (toLower(programSlug) === 'gmd') {
								goalsSurvey = getSurveyBySlug('gmd-goals', surveys);
							}
						}
					}

					let intakeSurvey = null;
					if (isListNotEmpty(surveys) && isStringNotEmpty(programSlug)) {
						intakeSurvey = getSurveyByEffectiveSurveyFlow({ program: resolvedContext.program, product: defaultProduct, productFamilies, surveys, flowKey: SURVEY_FLOW_KEYS.INTAKE });
						if (!isObjectNotEmpty(intakeSurvey)) {
							if (toLower(programSlug) === 'wl') {
								intakeSurvey = getSurveyBySlug('weight-loss-qualifying-form', surveys);
							} else if (toLower(programSlug) === 'l') {
								intakeSurvey = getSurveyBySlug('longevity-form4', surveys);
							} else if (toLower(programSlug) === 'ms') {
								if (toLower(productSku) === 'mic-b12' || toLower(productSku) === 'b12') {
									intakeSurvey = getSurveyBySlug('metabolic-support-form-mic-b12', surveys);
								} else {
									intakeSurvey = getSurveyBySlug('metabolic-support-form2', surveys);
								}
							} else if (toLower(programSlug) === 'fhrt') {
								intakeSurvey = getSurveyBySlug('fhrt-intake', surveys);
							} else if (toLower(programSlug) === 'sc') {
								intakeSurvey = getSurveyBySlug('sc-intake', surveys);
							} else if (toLower(programSlug) === 'gmd') {
								intakeSurvey = getSurveyBySlug('gmd-intake', surveys);
							}
						}
					}

					let leadForms = isListNotEmpty(newLead?.forms) ? [...newLead?.forms] : [];
					let orderedLeadForms = orderBy(leadForms, ['createdAt'], ['desc']);

					const goalsForm = orderedLeadForms?.find(i => i.surveyId === goalsSurvey?.id);

					const intakeForm = orderedLeadForms?.find(i => i.surveyId === intakeSurvey?.id);

					// a lead with no usable gender restarts at the beginning rather than resuming at
					// YOU_HAVE_QUALIFIED_STEP. everything from there to checkout is built around the
					// customer's sex - the pregnancy conditional, the product filter, and the customer
					// snapshot the prescriber reads - and there is no gender step in that stretch to collect
					// it, so resuming into it is how a lead reaches checkout without one
					const hasWritableGender = isWritableGender(toUpper(newLead?.gender ?? '')) || isWritableGender(toUpper(profile?.gender ?? '')) || isWritableGender(toUpper(gender ?? ''));

					if (!isNaN(newLead?.lastFunnelStep) && newLead?.lastFunnelStep > 1 && [15, 16, 32, 33, 17, 70, 71, 72].includes(newLead?.lastFunnelStep) && isObjectNotEmpty(goalsForm) && isObjectNotEmpty(intakeForm) && isBooleanTrue(hasWritableGender)) {
						await dispatch(setActiveStep(15)); // go to YOU_HAVE_QUALIFIED_STEP
						await dispatch(setCompletedSections(['goals']));
						await dispatch(setCurrentSection('intake'));
					} else {
						await dispatch(setActiveStep(0));
						await dispatch(setCurrentSection('goals'));
					}

					await dispatch(setIsFunnelLoading(false));

					const state2 = getState();
					const analyticsState2 = { ...state2.analyticsState };
					const { siteVisit } = analyticsState2;

					return { subscription: existingSubscription, encounter: existingEncounter, lead: newLead, siteVisit: siteVisit, funnelName: funnelName, programSlug: programSlug, productSku: productSku };
				} else {
					await dispatch(setLead(null));
					await dispatch(setLeadId(null));

					await dispatch(setActiveStep(0));
					await dispatch(setCurrentSection('goals'));

					await dispatch(setIsFunnelLoading(false));

					const state2 = getState();
					const analyticsState2 = { ...state2.analyticsState };
					const { siteVisit } = analyticsState2;

					return { subscription: existingSubscription, encounter: existingEncounter, lead: null, siteVisit: siteVisit, funnelName: funnelName, programSlug: programSlug, productSku: productSku };
				}
			} else {
				await dispatch(setActiveStep(0));
				await dispatch(setCurrentSection('goals'));

				await dispatch(setIsFunnelLoading(false));

				const state2 = getState();
				const analyticsState2 = { ...state2.analyticsState };
				const { siteVisit } = analyticsState2;

				return { subscription: null, encounter: null, lead: null, siteVisit: siteVisit, funnelName: funnelName, programSlug: programSlug, productSku: productSkuVal };
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);

			await dispatch(setIsFunnelLoading(false));
		}

		const state2 = getState();
		const analyticsState2 = { ...state2.analyticsState };
		const { siteVisit } = analyticsState2;

		return { subscription: null, encounter: null, lead: null, siteVisit: siteVisit, funnelName: funnelName, programSlug: programSlug, productSku: productSkuVal };
	}
};

export const setAddresses = (addresses) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_ADDRESSES', payload: addresses });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setSelectedAddress = (selectedAddress) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SELECTED_ADDRESS', payload: selectedAddress });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setShowAddressesModal = (showAddressesModal) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SHOW_ADDRESSES_MODAL', payload: showAddressesModal });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setShowNewAddressModal = (showNewAddressModal) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SHOW_NEW_ADDRESS_MODAL', payload: showNewAddressModal });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setShowEditAddressModal = (showEditAddressModal) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SHOW_EDIT_ADDRESS_MODAL', payload: showEditAddressModal });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setActiveStep = (activeStep) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_ACTIVE_STEP', payload: activeStep });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setCurrentSection = (currentSection) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_CURRENT_SECTION', payload: currentSection });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setCompletedSections = (completedSections) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_COMPLETED_SECTIONS', payload: completedSections });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setGoalsAnswers = (goalsAnswers) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_GOALS_ANSWERS', payload: goalsAnswers });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIntakeAnswers = (intakeAnswers) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_INTAKE_ANSWERS', payload: intakeAnswers });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setWeightLossAnswers = (weightLossAnswers) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_WEIGHT_LOSS_ANSWERS', payload: weightLossAnswers });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateWeightLossAnswers = (updatedFields) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const funnelState = { ...state.funnelState };
			const { weightLossAnswers } = funnelState;

			const existingWeightLossAnswers = isListNotEmpty(weightLossAnswers) ? { ...weightLossAnswers } : {};
			dispatch({ type: 'SET_WEIGHT_LOSS_ANSWERS', payload: { ...existingWeightLossAnswers, ...updatedFields } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setCurrentlyPregnant = (currentlyPregnant) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_CURRENTLY_PREGNANT', payload: currentlyPregnant });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setCutoffWeight = (cutoffWeight) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_CUTOFF_WEIGHT', payload: cutoffWeight });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setCurrentlyTakingGLP1 = (currentlyTakingGLP1) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_CURRENTLY_TAKING_GLP1', payload: currentlyTakingGLP1 });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setExperiencesSideEffects = (experiencesSideEffects) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_EXPERIENCES_SIDE_EFFECTS', payload: experiencesSideEffects });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setWantsAlternativeDosing = (wantsAlternativeDosing) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_WANTS_ALTERNATIVE_DOSING', payload: wantsAlternativeDosing });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setAvoidSideEffects = (avoidSideEffects) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_AVOID_SIDE_EFFECTS', payload: avoidSideEffects });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIntakeSteps = (intakeSteps) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_INTAKE_STEPS', payload: intakeSteps });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setActiveIntakeStep = (activeIntakeStep) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_ACTIVE_INTAKE_STEP', payload: activeIntakeStep });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setGoalsSteps = (goalsSteps) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_GOALS_STEPS', payload: goalsSteps });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setActiveGoalsStep = (activeGoalsStep) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_ACTIVE_GOALS_STEP', payload: activeGoalsStep });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setShowSignInModal = (showSignInModal) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SHOW_SIGN_IN_MODAL', payload: showSignInModal });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setShowMoreInfoModal = (showMoreInfoModal) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SHOW_MORE_INFO_MODAL', payload: showMoreInfoModal });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setShowWeightLossDisclaimerModal = (showWeightLossDisclaimerModal) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SHOW_WEIGHT_LOSS_DISCLAIMER_MODAL', payload: showWeightLossDisclaimerModal });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setProductSku = (productSku) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_PRODUCT_SKU', payload: productSku });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setSelectedProduct = (selectedProduct) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SELECTED_PRODUCT', payload: selectedProduct });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setSelectedFrequency = (selectedFrequency) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SELECTED_FREQUENCY', payload: selectedFrequency });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setAvailableProductSkus = (availableProductSkus) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_AVAILABLE_PRODUCT_SKUS', payload: availableProductSkus });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setUnenrolledProductSkus = (unenrolledProductSkus) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_UNENROLLED_PRODUCT_SKUS', payload: unenrolledProductSkus });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setSelectedVariantSku = (selectedVariantSku) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SELECTED_VARIANT_SKU', payload: selectedVariantSku });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setStartingLevel = (startingLevel) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_STARTING_LEVEL', payload: startingLevel });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setSubscription = (subscription) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_FUNNEL_SUBSCRIPTION', payload: subscription });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setEncounter = (encounter) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_FUNNEL_ENCOUNTER', payload: encounter });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setOrder = (order) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_FUNNEL_ORDER', payload: order });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setUserCouponCode = (userCouponCode) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_USER_COUPON_CODE', payload: userCouponCode });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setShowPaymentScheduleModal = (showPaymentScheduleModal) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SHOW_PAYMENT_SCHEDULE_MODAL', payload: showPaymentScheduleModal });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setBillingSameAsShipping = (billingSameAsShipping) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_BILLING_SAME_AS_SHIPPING', payload: billingSameAsShipping });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setPaymentMethods = (paymentMethods) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_PAYMENT_METHODS', payload: paymentMethods });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setPaymentMethodType = (paymentMethodType) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_PAYMENT_METHOD_TYPE', payload: paymentMethodType });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setShowKlarnaPaymentMethod = (showKlarnaPaymentMethod) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_SHOW_KLARNA_PAYMENT_METHOD', payload: showKlarnaPaymentMethod });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIsFunnelLoading = (isFunnelLoading) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_IS_FUNNEL_LOADING', payload: isFunnelLoading });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIsFunnelLoadingMessage = (isFunnelLoadingMessage) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_IS_FUNNEL_LOADING_MESSAGE', payload: isFunnelLoadingMessage });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIsAccountLoading = (isAccountLoading) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_IS_ACCOUNT_LOADING', payload: isAccountLoading });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIsAccountLoadingMessage = (isAccountLoadingMessage) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_IS_ACCOUNT_LOADING_MESSAGE', payload: isAccountLoadingMessage });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIsEligibilityLoading = (isEligibilityLoading) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_IS_ELIGIBILITY_LOADING', payload: isEligibilityLoading });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIsEligibilityLoadingMessage = (isEligibilityLoadingMessage) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_IS_ELIGIBILITY_LOADING_MESSAGE', payload: isEligibilityLoadingMessage });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setIsAddressLoading = (isAddressLoading) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_IS_ADDRESS_LOADING', payload: isAddressLoading });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setAddressError = (addressError) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_ADDRESS_ERROR', payload: addressError });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setFunnelError = (funnelError) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_FUNNEL_ERROR', payload: funnelError });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setProgramSlug = (programSlug) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_FUNNEL_PROGRAM_SLUG', payload: programSlug });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setAddon = (addon) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_FUNNEL_ADDON', payload: addon });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setVersion = (version) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'SET_FUNNEL_VERSION', payload: version });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const resetFunnel = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'RESET_FUNNEL', payload: null });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const onChangeHeight = (value) => {
	return async (dispatch) => {
		try {
			dispatch(setHeight(value));
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const onChangeWeight = (value) => {
	return async (dispatch) => {
		try {
			dispatch(setWeight(value));
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const onChangeVariantSku = (variantSku) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const funnelState = { ...state.funnelState };
			const analyticsState = { ...state.analyticsState };
			const orchestrator = { ...state.orchestrator };
			const { product } = analyticsState;
			const { selectedProduct } = funnelState;
			const { variants, products } = orchestrator;

			// console.log('onChangeVariantSku', variantSku, product, selectedProduct);
			// if (isObjectNotEmpty(selectedProduct) && isListNotEmpty(variants) && isStringNotEmpty(variantSku) && isObjectNotEmpty(variants?.find(i => i.productId === selectedProduct?.id && isBooleanTrue(i.enabled) && i.sku === variantSku))) {
			// 	await dispatch(setSelectedVariantSku(variantSku));
			// 	await dispatch(setSelectedProduct(selectedProduct));
			// 	await dispatch(setSelectedFrequency(1));
			// 	await dispatch(addSingleItemToCart(selectedProduct, 1, variants?.find(i => i.productId === selectedProduct?.id && isBooleanTrue(i.enabled) && i.sku === variantSku) ?? null));
			// } else if (isObjectNotEmpty(product) && isListNotEmpty(variants) && isStringNotEmpty(variantSku) && isObjectNotEmpty(variants?.find(i => i.productId === product?.id && isBooleanTrue(i.enabled) && i.sku === variantSku))) {
			// 	await dispatch(setSelectedVariantSku(variantSku));
			// 	await dispatch(setSelectedProduct(product));
			// 	await dispatch(setSelectedFrequency(1));
			// 	await dispatch(addSingleItemToCart(product, 1, variants?.find(i => i.productId === product?.id && isBooleanTrue(i.enabled) && i.sku === variantSku) ?? null));
			// } else 
			const selectedVariant = variants?.find(i => isBooleanTrue(i.enabled) && i.sku === variantSku) ?? null;
			const selectedProductVal = products?.find(i => i.id === selectedVariant?.productId) ?? selectedProduct ?? product;
			// console.log('onChangeVariantSku', selectedVariant, selectedProductVal);

			if (isStringNotEmpty(variantSku) && isObjectNotEmpty(variants?.find(i => isBooleanTrue(i.enabled) && i.sku === variantSku))) {
				await dispatch(setSelectedVariantSku(variantSku));
				await dispatch(setSelectedProduct(selectedProductVal));
				await dispatch(setSelectedFrequency(1));
				await dispatch(addSingleItemToCart(selectedProductVal, 1, selectedVariant));
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const onChangeProduct = (productSku) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const funnelState = { ...state.funnelState };
			const orchestrator = { ...state.orchestrator };
			const authState = { ...state.authState };

			const { intakeAnswers, leadDocuments } = funnelState;
			const { products, variants } = orchestrator;
			const { profile } = authState;

			let product = getProductBySku(productSku, products);
			// console.log('onChangeProduct productSku', productSku, product);
			if (isObjectNotEmpty(product) && isStringNotEmpty(product?.id)) {
				await dispatch(setProductSku(productSku));

				let startingVariantLocal = null;
				if (leadDocuments?.some(i => i.documentType === 'MEDICATION')) {
					let currentIntakeAnswers = isListNotEmpty(intakeAnswers) ? [...intakeAnswers] : [];
					const currentlyTakingGLP1MedicationField = currentIntakeAnswers.find(i => i.customName === 'currentlyTakingGLP1Medication');
					// console.log('currentlyTakingGLP1MedicationField', currentlyTakingGLP1MedicationField);
					if (isObjectNotEmpty(product) && isObjectNotEmpty(currentlyTakingGLP1MedicationField) && currentlyTakingGLP1MedicationField?.value === 'YES') {
						// is on a GLP-1 medication already so figure out what level to start them at
						const currentGLP1MedicationField = currentIntakeAnswers.find(i => i.customName === 'currentGLP1Medication');
						// console.log('currentGLP1MedicationField', currentGLP1MedicationField);
						const semaglutideProduct = getProductBySku('SEMAGLUTIDE-G', products);
						const tirzepatideProduct = getProductBySku('TIRZEPATIDE-G', products);
						const ozempicProduct = getProductBySku('OZEMPIC', products);
						const zepboundProduct = getProductBySku('ZEPBOUND', products);

						let currentDosageVal = null;
						let nextDosageVal = null;
						let nextVariant = null;
						if (isObjectNotEmpty(currentGLP1MedicationField) && ['COMPOUNDED_SEMAGLUTIDE', 'OZEMPIC_SEMAGLUTIDE', 'WEGOVY_SEMAGLUTIDE', 'RYBELSUS_SEMAGLUTIDE'].includes(currentGLP1MedicationField?.value)) {
							// current medication is a semaglutide
							const currentDosageField = currentIntakeAnswers.find(i => i.customName === 'currentSemaglutideDosage');
							const nextDosageField = currentIntakeAnswers.find(i => i.customName === 'nextSemaglutideDosage');
							currentDosageVal = currentDosageField?.value;
							nextDosageVal = nextDosageField?.value;
						} else if (isObjectNotEmpty(currentGLP1MedicationField) && ['COMPOUNDED_TIRZEPATIDE', 'ZEPBOUND_TIRZEPATIDE', 'MOUNJARO_TIRZEPATIDE'].includes(currentGLP1MedicationField?.value)) {
							// current medication is a tirzepatide
							const currentDosageField = currentIntakeAnswers.find(i => i.customName === 'currentTirzepatideDosage');
							const nextDosageField = currentIntakeAnswers.find(i => i.customName === 'nextTirzepatideDosage');
							currentDosageVal = currentDosageField?.value;
							nextDosageVal = nextDosageField?.value;
						}

						if (isObjectNotEmpty(semaglutideProduct) && productSku === semaglutideProduct?.sku && isListNotEmpty(variants)) {
							let nextDosage = getNextCompoundedSemaglutideDosage(currentDosageVal, nextDosageVal);
							nextVariant = variants.find(i => i.productId === semaglutideProduct?.id && isBooleanTrue(i.enabled) && i.strength === nextDosage);
						} else if (isObjectNotEmpty(ozempicProduct) && productSku === ozempicProduct?.sku && isListNotEmpty(variants)) {
							let nextDosage = getNextOzempicDosage(currentDosageVal, nextDosageVal);
							nextVariant = variants.find(i => i.productId === ozempicProduct?.id && isBooleanTrue(i.enabled) && i.strength === nextDosage);
						} else if (isObjectNotEmpty(zepboundProduct) && productSku === zepboundProduct?.sku && isListNotEmpty(variants)) {
							let nextDosage = getNextZepboundDosage(currentDosageVal, nextDosageVal);
							nextVariant = variants.find(i => i.productId === zepboundProduct?.id && isBooleanTrue(i.enabled) && i.strength === nextDosage);
						} else if (isObjectNotEmpty(tirzepatideProduct) && productSku === tirzepatideProduct?.sku && isListNotEmpty(variants)) {
							let nextDosage = getNextCompoundedTirzepatideDosage(currentDosageVal, nextDosageVal);
							nextVariant = variants.find(i => i.productId === tirzepatideProduct?.id && isBooleanTrue(i.enabled) && i.strength === nextDosage);
						}

						if (isObjectNotEmpty(nextVariant)) {
							startingVariantLocal = nextVariant;
						}
					}
				} else {
					const semaglutideProduct = getProductBySku('SEMAGLUTIDE-G', products);
					const tirzepatideProduct = getProductBySku('TIRZEPATIDE-G', products);
					const ozempicProduct = getProductBySku('OZEMPIC', products);
					const zepboundProduct = getProductBySku('ZEPBOUND', products);
					const semaglutideMicrodosingProduct = getProductBySku('SEMAGLUTIDE-G-MICRODOSING', products);
					const tirzepatideMicrodosingProduct = getProductBySku('TIRZEPATIDE-G-MICRODOSING', products);

					let nextVariant = null;
					if (isObjectNotEmpty(semaglutideProduct) && productSku === semaglutideProduct?.sku && isListNotEmpty(variants)) {
						nextVariant = variants.find(i => i.productId === semaglutideProduct?.id && isBooleanTrue(i.enabled) && i.titrationLevel === 1 && i.titrationSchedule === 'ALTERNATIVE');
					} else if (isObjectNotEmpty(ozempicProduct) && productSku === ozempicProduct?.sku && isListNotEmpty(variants)) {
						nextVariant = variants.find(i => i.productId === ozempicProduct?.id && isBooleanTrue(i.enabled) && i.titrationLevel === 1);
					} else if (isObjectNotEmpty(zepboundProduct) && productSku === zepboundProduct?.sku && isListNotEmpty(variants)) {
						nextVariant = variants.find(i => i.productId === zepboundProduct?.id && isBooleanTrue(i.enabled) && i.titrationLevel === 1);
					} else if (isObjectNotEmpty(tirzepatideProduct) && productSku === tirzepatideProduct?.sku && isListNotEmpty(variants)) {
						nextVariant = variants.find(i => i.productId === tirzepatideProduct?.id && isBooleanTrue(i.enabled) && i.titrationLevel === 1 && i.titrationSchedule === 'ALTERNATIVE');
					} else if (isObjectNotEmpty(semaglutideMicrodosingProduct) && productSku === semaglutideMicrodosingProduct?.sku && isListNotEmpty(variants)) {
						nextVariant = variants.find(i => i.productId === semaglutideMicrodosingProduct?.id && isBooleanTrue(i.enabled) && i.titrationLevel === 1 && i.titrationSchedule === 'ALTERNATIVE');
					} else if (isObjectNotEmpty(tirzepatideMicrodosingProduct) && productSku === tirzepatideMicrodosingProduct?.sku && isListNotEmpty(variants)) {
						nextVariant = variants.find(i => i.productId === tirzepatideMicrodosingProduct?.id && isBooleanTrue(i.enabled) && i.titrationLevel === 1 && i.titrationSchedule === 'ALTERNATIVE');
					}

					if (isObjectNotEmpty(nextVariant)) {
						startingVariantLocal = nextVariant;
					}
				}

				if (isObjectEmpty(startingVariantLocal)) {
					startingVariantLocal = getDefaultEnabledVariantForFunnelProduct(product, variants);
				}

				let defaultBundleCount = toMaxBundleCount(product, profile);
				// console.log('startingVariantLocal', startingVariantLocal, startingVariantLocal?.sku);
				await dispatch(setSelectedVariantSku(startingVariantLocal?.sku));
				await dispatch(setSelectedProduct(product));
				await dispatch(addSingleItemToCart(product, defaultBundleCount, startingVariantLocal));
				await dispatch(onChangeFrequency(defaultBundleCount, product?.id));
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const onChangeFrequency = (frequency, productId) => {
	return async (dispatch) => {
		try {
			// console.log(value);
			// console.log(typeof value)
			if (isStringNotEmpty(productId)) {
				if (!isNaN(frequency)) {
					await dispatch(setSelectedFrequency(Number(frequency)));
					if (Number(frequency) > 1) {
						await dispatch(changeBundleCount(productId, Number(frequency)));
					} else {
						await dispatch(changeBundleCount(productId, 1));
					}
				} else {
					await dispatch(setSelectedFrequency(Number(1)));
					await dispatch(changeBundleCount(productId, 1));
				}
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

//#region helper methods

export const getNextCompoundedSemaglutideDosage = (currentStrength, nextDirection) => {
	if (nextDirection === 'SAME_1' || nextDirection === 'SAME_3') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '0.25mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '0.5mg';
			case 'SEMAGLUTIDE_1MG':
				return '1mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '1.7mg';
			case 'SEMAGLUTIDE_2MG':
				return '1.7mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '2.4mg';
			case 'TIRZEPATIDE_2_5MG':
				return '0.25mg';
			case 'TIRZEPATIDE_5MG':
				return '1mg';
			case 'TIRZEPATIDE_7_5MG':
				return '2.4mg';
			case 'TIRZEPATIDE_10MG':
				return '2.4mg';
			case 'TIRZEPATIDE_12_5MG':
				return '2.4mg';
			case 'TIRZEPATIDE_15MG':
				return '2.4mg';
			default:
				return '0.25mg';
		}
	} else if (nextDirection === 'DECREASE') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '0.25mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '0.25mg';
			case 'SEMAGLUTIDE_1MG':
				return '0.5mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '1mg';
			case 'SEMAGLUTIDE_2MG':
				return '1mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '1.7mg';
			case 'TIRZEPATIDE_2_5MG':
				return '0.25mg';
			case 'TIRZEPATIDE_5MG':
				return '0.5mg';
			case 'TIRZEPATIDE_7_5MG':
				return '1.7mg';
			case 'TIRZEPATIDE_10MG':
				return '2.4mg';
			case 'TIRZEPATIDE_12_5MG':
				return '2.4mg';
			case 'TIRZEPATIDE_15MG':
				return '2.4mg';
			default:
				return '0.25mg';
		}
	} else if (nextDirection === 'INCREASE') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '0.5mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '1mg';
			case 'SEMAGLUTIDE_1MG':
				return '1.7mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '2.4mg';
			case 'SEMAGLUTIDE_2MG':
				return '2.4mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '2.4mg';
			case 'TIRZEPATIDE_2_5MG':
				return '0.5mg';
			case 'TIRZEPATIDE_5MG':
				return '1.7mg';
			case 'TIRZEPATIDE_7_5MG':
				return '2.4mg';
			case 'TIRZEPATIDE_10MG':
				return '2.4mg';
			case 'TIRZEPATIDE_12_5MG':
				return '2.4mg';
			case 'TIRZEPATIDE_15MG':
				return '2.4mg';
			default:
				return '0.25mg';
		}
	} else {
		return '0.25mg';
	}
};

export const getNextCompoundedTirzepatideDosage = (currentStrength, nextDirection) => {
	if (nextDirection === 'SAME_1' || nextDirection === 'SAME_3') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_1MG':
				return '5mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '5mg';
			case 'SEMAGLUTIDE_2MG':
				return '5mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '7.5mg';
			case 'TIRZEPATIDE_2_5MG':
				return '2.5mg';
			case 'TIRZEPATIDE_5MG':
				return '5mg';
			case 'TIRZEPATIDE_7_5MG':
				return '7.5mg';
			case 'TIRZEPATIDE_10MG':
				return '10mg';
			case 'TIRZEPATIDE_12_5MG':
				return '12.5mg';
			case 'TIRZEPATIDE_15MG':
				return '15mg';
			default:
				return '2.5mg';
		}
	} else if (nextDirection === 'DECREASE') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_1MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '5mg';
			case 'SEMAGLUTIDE_2MG':
				return '5mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '5mg';
			case 'TIRZEPATIDE_2_5MG':
				return '2.5mg';
			case 'TIRZEPATIDE_5MG':
				return '2.5mg';
			case 'TIRZEPATIDE_7_5MG':
				return '5mg';
			case 'TIRZEPATIDE_10MG':
				return '7.5mg';
			case 'TIRZEPATIDE_12_5MG':
				return '10mg';
			case 'TIRZEPATIDE_15MG':
				return '12.5mg';
			default:
				return '2.5mg';
		}
	} else if (nextDirection === 'INCREASE') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '5mg';
			case 'SEMAGLUTIDE_1MG':
				return '5mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '7.5mg';
			case 'SEMAGLUTIDE_2MG':
				return '7.5mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '7.5mg';
			case 'TIRZEPATIDE_2_5MG':
				return '5mg';
			case 'TIRZEPATIDE_5MG':
				return '7.5mg';
			case 'TIRZEPATIDE_7_5MG':
				return '10mg';
			case 'TIRZEPATIDE_10MG':
				return '12.5mg';
			case 'TIRZEPATIDE_12_5MG':
				return '15mg';
			case 'TIRZEPATIDE_15MG':
				return '15mg';
			default:
				return '2.5mg';
		}
	} else {
		return '2.5mg';
	}
};

export const getNextCompoundedSemaglutideGMDosage = (currentStrength, nextDirection, titrationSchedule, hasSideEffects) => {
	if ((nextDirection === 'SAME_1' || nextDirection === 'SAME_3') && titrationSchedule !== 'RAPID') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '0.2mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_0_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '0.6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_1MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '0.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '1.12mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_1_7MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '1.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_2MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '1.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '2.16mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_2_4MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));

			case 'SEMAGLUTIDE_0_2MG':
				return '0.2mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_4MG':
				return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_6MG':
				return '0.6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_9MG':
				return '0.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_12MG':
				return '1.12mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_5MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_9MG':
				return '1.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_16MG':
				return '2.16mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_3MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));

			case 'TIRZEPATIDE_2_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '0.2mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '0.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '1.12mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_7_5MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'TIRZEPATIDE_10MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'TIRZEPATIDE_12_5MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'TIRZEPATIDE_15MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));

			case 'TIRZEPATIDE_1_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '0.2mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_3MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '0.6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_6MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '0.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '1.12mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_9MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'TIRZEPATIDE_11MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'TIRZEPATIDE_13_5MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));

			default:
				return '0.2mg/0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
		}
	} else if (nextDirection === 'DECREASE' && titrationSchedule !== 'RAPID') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '0.2mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_5MG':
				return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1MG':
				return '0.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_7MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2MG':
				return '1.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_4MG':
				return '2.16mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'SEMAGLUTIDE_0_2MG':
				return '0.2mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_4MG':
				return '0.2mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_6MG':
				return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_9MG':
				return '0.6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_12MG':
				return '0.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_5MG':
				return '1.12mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_9MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_16MG':
				return '1.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_3MG':
				return '2.16mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'TIRZEPATIDE_2_5MG':
				return '0.2mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_5MG':
				return '0.6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_7_5MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_10MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_12_5MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_15MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'TIRZEPATIDE_1_5MG':
				return '0.2mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_3MG':
				return '0.2mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_6MG':
				return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_9MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_11MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_13_5MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			default:
				return '0.2mg/0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
		}
	} else if (nextDirection === 'INCREASE' || titrationSchedule === 'RAPID') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else if (titrationSchedule === 'RAPID') {
					return '0.6mg/0.9mg Rapid';
				} else {
					return '0.6mg/0.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_0_5MG':
				if (titrationSchedule === 'RAPID') {
					return '1.12mg/1.5mg Rapid';
				} else {
					return '0.6mg/0.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_1MG':
				if (titrationSchedule === 'RAPID') {
					return '1.9mg/2.16mg Rapid';
				} else {
					return '1.12mg/1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_1_7MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '1.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else if (titrationSchedule === 'RAPID') {
					return '2.3mg Rapid';
				} else {
					return '1.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_2MG':
				if (titrationSchedule === 'RAPID') {
					return '2.3mg Rapid';
				} else {
					return '2.16mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_2_4MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));

			case 'SEMAGLUTIDE_0_2MG':
				return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_4MG':
				return '0.6mg/0.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'SEMAGLUTIDE_0_6MG':
				return '0.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_9MG':
				return '1.12mg/1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'SEMAGLUTIDE_1_12MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_5MG':
				if (titrationSchedule === 'RAPID') {
					return '1.9mg/2.16mg Rapid';
				} else {
					return '1.9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_1_9MG':
				return '2.16mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_16MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'SEMAGLUTIDE_2_3MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));

			case 'TIRZEPATIDE_2_5MG':
				return '0.6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_5MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_7_5MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'TIRZEPATIDE_10MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'TIRZEPATIDE_12_5MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'TIRZEPATIDE_15MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));

			case 'TIRZEPATIDE_1_5MG':
				return '0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_3MG':
				return '0.6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_6MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_9MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'TIRZEPATIDE_11MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
			case 'TIRZEPATIDE_13_5MG':
				return '2.3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));

			default:
				return '0.2mg/0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
		}
	} else {
		return '0.2mg/0.4mg' + (titrationSchedule === 'SPLIT' ? ' Split' : (titrationSchedule === 'RAPID' ? ' Rapid' : ''));
	}
};

export const getNextOzempicDosage = (currentStrength, nextDirection) => {
	if (nextDirection === 'SAME_1' || nextDirection === 'SAME_3') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '0.25mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '0.5mg';
			case 'SEMAGLUTIDE_1MG':
				return '1mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '2mg';
			case 'SEMAGLUTIDE_2MG':
				return '2mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '2mg';

			case 'TIRZEPATIDE_2_5MG':
				return '0.25mg';
			case 'TIRZEPATIDE_5MG':
				return '1mg';
			case 'TIRZEPATIDE_7_5MG':
				return '2mg';
			case 'TIRZEPATIDE_10MG':
				return '2mg';
			case 'TIRZEPATIDE_12_5MG':
				return '2mg';
			case 'TIRZEPATIDE_15MG':
				return '2mg';

			case 'TIRZEPATIDE_1_5MG':
				return '0.25mg';
			case 'TIRZEPATIDE_3MG':
				return '0.25mg';
			case 'TIRZEPATIDE_6MG':
				return '1mg';
			case 'TIRZEPATIDE_9MG':
				return '2mg';
			case 'TIRZEPATIDE_11MG':
				return '2mg';
			case 'TIRZEPATIDE_13_5MG':
				return '2mg';

			default:
				return '0.25mg';
		}
	} else if (nextDirection === 'DECREASE') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '0.25mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '0.25mg';
			case 'SEMAGLUTIDE_1MG':
				return '0.5mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '1mg';
			case 'SEMAGLUTIDE_2MG':
				return '1mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '2mg';

			case 'TIRZEPATIDE_2_5MG':
				return '0.25mg';
			case 'TIRZEPATIDE_5MG':
				return '0.5mg';
			case 'TIRZEPATIDE_7_5MG':
				return '1mg';
			case 'TIRZEPATIDE_10MG':
				return '2mg';
			case 'TIRZEPATIDE_12_5MG':
				return '2mg';
			case 'TIRZEPATIDE_15MG':
				return '2mg';

			case 'TIRZEPATIDE_1_5MG':
				return '0.25mg';
			case 'TIRZEPATIDE_3MG':
				return '0.25mg';
			case 'TIRZEPATIDE_6MG':
				return '0.5mg';
			case 'TIRZEPATIDE_9MG':
				return '1mg';
			case 'TIRZEPATIDE_11MG':
				return '2mg';
			case 'TIRZEPATIDE_13_5MG':
				return '2mg';

			default:
				return '0.25mg';
		}
	} else if (nextDirection === 'INCREASE') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '0.5mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '1mg';
			case 'SEMAGLUTIDE_1MG':
				return '2mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '2mg';
			case 'SEMAGLUTIDE_2MG':
				return '2mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '2mg';

			case 'TIRZEPATIDE_2_5MG':
				return '0.5mg';
			case 'TIRZEPATIDE_5MG':
				return '2mg';
			case 'TIRZEPATIDE_7_5MG':
				return '2mg';
			case 'TIRZEPATIDE_10MG':
				return '2mg';
			case 'TIRZEPATIDE_12_5MG':
				return '2mg';
			case 'TIRZEPATIDE_15MG':
				return '2mg';

			case 'TIRZEPATIDE_1_5MG':
				return '0.5mg';
			case 'TIRZEPATIDE_3MG':
				return '0.5mg';
			case 'TIRZEPATIDE_6MG':
				return '2mg';
			case 'TIRZEPATIDE_9MG':
				return '2mg';
			case 'TIRZEPATIDE_11MG':
				return '2mg';
			case 'TIRZEPATIDE_13_5MG':
				return '2mg';

			default:
				return '0.25mg';
		}
	} else {
		return '0.25mg';
	}
};

export const getNextCompoundedTirzepatideGMDosage = (currentStrength, nextDirection, titrationSchedule, hasSideEffects) => {
	if ((nextDirection === 'SAME_1' || nextDirection === 'SAME_3') && titrationSchedule !== 'RAPID') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_0_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_1MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_1_7MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_2MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_2_4MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}

			case 'SEMAGLUTIDE_0_2MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_4MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_6MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_9MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_12MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_5MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_9MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_16MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_3MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'TIRZEPATIDE_1_5MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_3MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_6MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_9MG':
				return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_11MG':
				return '11mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_13_5MG':
				return '13.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'TIRZEPATIDE_2_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_7_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_10MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '11mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_12_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '11mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '13.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_15MG':
				return '13.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			default:
				return '1.5mg/3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
		}
	} else if (nextDirection === 'DECREASE' && titrationSchedule !== 'RAPID') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_5MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_7MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_4MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'SEMAGLUTIDE_0_2MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_4MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_6MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_9MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_12MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_5MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_9MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_16MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_3MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'TIRZEPATIDE_2_5MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_5MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_7_5MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_10MG':
				return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_12_5MG':
				return '11mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_15MG':
				return '13.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'TIRZEPATIDE_1_5MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_3MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_6MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_9MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_11MG':
				return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_13_5MG':
				return '11mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			default:
				return '1.5mg/3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
		}
	} else if (nextDirection === 'INCREASE' || titrationSchedule === 'RAPID') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_0_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_1MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'SEMAGLUTIDE_1_7MG':
				return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2MG':
				return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_4MG':
				return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'SEMAGLUTIDE_0_2MG':
				return '1.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_4MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_6MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_0_9MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_12MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_5MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_1_9MG':
				return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_16MG':
				return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'SEMAGLUTIDE_2_3MG':
				return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'TIRZEPATIDE_2_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				} else {
					return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_7_5MG':
				return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_10MG':
				return '11mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_12_5MG':
				return '13.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_15MG':
				return '13.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			case 'TIRZEPATIDE_1_5MG':
				return '3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_3MG':
				if (titrationSchedule === 'RAPID') {
					return '6mg/9mg Rapid';
				} else {
					return '6mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_6MG':
				return '9mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_9MG':
				if (titrationSchedule === 'RAPID') {
					return '11mg/13.5mg Rapid';
				} else {
					return '11mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
				}
			case 'TIRZEPATIDE_11MG':
				return '13.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
			case 'TIRZEPATIDE_13_5MG':
				return '13.5mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');

			default:
				return '1.5mg/3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
		}
	} else {
		return '1.5mg/3mg' + (titrationSchedule === 'SPLIT' ? ' Split' : '');
	}
};

export const getNextZepboundDosage = (currentStrength, nextDirection, hasSideEffects) => {
	if (nextDirection === 'SAME_1' || nextDirection === 'SAME_3') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_1MG':
				return '5mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '5mg';
			case 'SEMAGLUTIDE_2MG':
				return '5mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '7.5mg';

			case 'TIRZEPATIDE_2_5MG':
				return '2.5mg';
			case 'TIRZEPATIDE_5MG':
				return '5mg';
			case 'TIRZEPATIDE_7_5MG':
				return '7.5mg';
			case 'TIRZEPATIDE_10MG':
				return '10mg';
			case 'TIRZEPATIDE_12_5MG':
				return '12.5mg';
			case 'TIRZEPATIDE_15MG':
				return '15mg';

			case 'TIRZEPATIDE_1_5MG':
				return '2.5mg';
			case 'TIRZEPATIDE_3MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '2.5mg';
				} else {
					return '5mg';
				}
			case 'TIRZEPATIDE_6MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '5mg';
				} else {
					return '7.5mg';
				}
			case 'TIRZEPATIDE_9MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '7.5mg';
				} else {
					return '10mg';
				}
			case 'TIRZEPATIDE_11MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '10mg';
				} else {
					return '12.5mg';
				}
			case 'TIRZEPATIDE_13_5MG':
				if (hasSideEffects === 'SIDE_EFFECTS') {
					return '12.5mg';
				} else {
					return '15mg';
				}

			default:
				return '2.5mg';
		}
	} else if (nextDirection === 'DECREASE') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_1MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '5mg';
			case 'SEMAGLUTIDE_2MG':
				return '5mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '5mg';


			case 'TIRZEPATIDE_2_5MG':
				return '2.5mg';
			case 'TIRZEPATIDE_5MG':
				return '2.5mg';
			case 'TIRZEPATIDE_7_5MG':
				return '5mg';
			case 'TIRZEPATIDE_10MG':
				return '7.5mg';
			case 'TIRZEPATIDE_12_5MG':
				return '10mg';
			case 'TIRZEPATIDE_15MG':
				return '12.5mg';

			case 'TIRZEPATIDE_1_5MG':
				return '2.5mg';
			case 'TIRZEPATIDE_3MG':
				return '2.5mg';
			case 'TIRZEPATIDE_6MG':
				return '5mg';
			case 'TIRZEPATIDE_9MG':
				return '7.5mg';
			case 'TIRZEPATIDE_11MG':
				return '10mg';
			case 'TIRZEPATIDE_13_5MG':
				return '12.5mg';

			default:
				return '2.5mg';
		}
	} else if (nextDirection === 'INCREASE') {
		switch (currentStrength) {
			case 'SEMAGLUTIDE_0_25MG':
				return '2.5mg';
			case 'SEMAGLUTIDE_0_5MG':
				return '5mg';
			case 'SEMAGLUTIDE_1MG':
				return '5mg';
			case 'SEMAGLUTIDE_1_7MG':
				return '7.5mg';
			case 'SEMAGLUTIDE_2MG':
				return '7.5mg';
			case 'SEMAGLUTIDE_2_4MG':
				return '7.5mg';

			case 'TIRZEPATIDE_2_5MG':
				return '5mg';
			case 'TIRZEPATIDE_5MG':
				return '7.5mg';
			case 'TIRZEPATIDE_7_5MG':
				return '10mg';
			case 'TIRZEPATIDE_10MG':
				return '12.5mg';
			case 'TIRZEPATIDE_12_5MG':
				return '15mg';
			case 'TIRZEPATIDE_15MG':
				return '15mg';

			case 'TIRZEPATIDE_1_5MG':
				return '2.5mg';
			case 'TIRZEPATIDE_3MG':
				return '5mg';
			case 'TIRZEPATIDE_6MG':
				return '7.5mg';
			case 'TIRZEPATIDE_9MG':
				return '10mg';
			case 'TIRZEPATIDE_11MG':
				return '12.5mg';
			case 'TIRZEPATIDE_13_5MG':
				return '15mg';

			default:
				return '2.5mg';
		}
	} else {
		return '2.5mg';
	}
};

//#endregion

export const useFunnel = () => {
	//#region useDispatch

	const dispatch = useDispatch();

	//#endregion
	//#region useAuth

	const { isFoundInCartBySku } = useCart();

	//#endregion
	//#region useSelectors


	const lead = useSelector((appState) => appState.analyticsState.lead);
	const campaignSlug = useSelector((appState) => appState.analyticsState.campaignSlug);
	const productCategorySlug = useSelector((appState) => appState.analyticsState.productCategorySlug);
	const testMode = useSelector((appState) => appState.analyticsState.testMode);

	const profile = useSelector((appState) => appState.authState.profile);
	const shippingState = useSelector((appState) => appState.authState.shippingState);
	const gender = useSelector((appState) => appState.authState.gender);
	const dateOfBirth = useSelector((appState) => appState.authState.dateOfBirth);
	const height = useSelector((appState) => appState.authState.height);
	const weight = useSelector((appState) => appState.authState.weight);
	const BMI = useSelector((appState) => appState.authState.BMI);
	const firstName = useSelector((appState) => appState.authState.firstName);
	const lastName = useSelector((appState) => appState.authState.lastName);
	const phoneVerified = useSelector((appState) => appState.authState.phoneVerified);
	const currentAuthUser = useSelector((appState) => appState.authState.currentAuthUser);

	const cartItems = useSelector((appState) => appState.cartState.cartItems);
	const coupon = useSelector((appState) => appState.cartState.coupon);
	const couponApplied = useSelector((appState) => appState.cartState.couponApplied);
	const couponCodeApplied = useSelector((appState) => appState.cartState.couponCodeApplied);
	const shippingAddress = useSelector((appState) => appState.cartState.shippingAddress);
	const taxRate = useSelector((appState) => appState.cartState.taxRate);
	const cartSummary = useSelector((appState) => appState.cartState.cartSummary);
	const shippingMethod = useSelector((appState) => appState.cartState.shippingMethod);
	const billingAddress = useSelector((appState) => appState.cartState.billingAddress);
	const paymentMethod = useSelector((appState) => appState.cartState.paymentMethod);

	const activeStep = useSelector((appState) => appState.funnelState.activeStep);
	const activeIntakeStep = useSelector((appState) => appState.funnelState.activeIntakeStep);
	const goalsAnswers = useSelector((appState) => appState.funnelState.goalsAnswers);
	const intakeAnswers = useSelector((appState) => appState.funnelState.intakeAnswers);
	const currentlyPregnant = useSelector((appState) => appState.funnelState.currentlyPregnant);

	const programSlug = useSelector((appState) => appState.funnelState.programSlug);
	const productSku = useSelector((appState) => appState.funnelState.productSku);
	const version = useSelector((appState) => appState.funnelState.version);
	const selectedVariantSku = useSelector((appState) => appState.funnelState.selectedVariantSku);
	const selectedProduct = useSelector((appState) => appState.funnelState.selectedProduct);
	const selectedFrequency = useSelector((appState) => appState.funnelState.selectedFrequency);

	const products = useSelector((appState) => appState.orchestrator.products);
	const variants = useSelector((appState) => appState.orchestrator.variants);
	const productCategories = useSelector((appState) => appState.orchestrator.productCategories);
	const surveys = useSelector((appState) => appState.orchestrator.surveys);
	const shippingMethods = useSelector((appState) => appState.orchestrator.shippingMethods);
	const campaigns = useSelector((appState) => appState.orchestrator.campaigns);
	const programs = useSelector((appState) => appState.orchestrator.programs);
	const productFamilies = useSelector((appState) => appState.orchestrator.productFamilies);
	const offerGroups = useSelector((appState) => appState.orchestrator.offerGroups);
	const funnelChoiceSets = useSelector((appState) => appState.orchestrator.funnelChoiceSets);

	const program = useSelector(state => selectProgramBySlug(state, programSlug));
	const programProducts = useSelector(appState => selectProductsByProgramSlug(appState, programSlug));
	const product = useSelector(state => selectDefaultProductByProgramSlug(state, programSlug, productSku));
	const campaign = useSelector(state => selectDefaultCampaignByProgramSlug(state, programSlug, campaignSlug));
	const productCategory = useSelector(state => selectProductCategoryBySlug(state, productCategorySlug));

	const nmiErrors = useSelector((appState) => appState.collectJSState.errors);

	//#endregion
	//#region useMemos

	const programId = useMemo(() => {
		if (isStringNotEmpty(program?.id)) {
			return program?.id;
		}

		return 'none';
	}, [program?.id]);

	const campaignId = useMemo(() => {
		if (isStringNotEmpty(campaign?.id)) {
			return campaign?.id;
		}

		return 'none';
	}, [campaign?.id]);

	const productId = useMemo(() => {
		if (isStringNotEmpty(product?.id)) {
			return product?.id;
		}

		return null;
	}, [product?.id]);

	const productCategoryId = useMemo(() => {
		if (isStringNotEmpty(productCategory?.id)) {
			return productCategory?.id;
		}

		return 'none';
	}, [productCategory?.id]);

	const funnelName = useMemo(() => {
		if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'wl' && isStringNotEmpty(version)) {
			return `WL_INTAKE-${version}`;
		} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'wl' && isStringEmpty(version)) {
			return `WL_INTAKE-5`; // default funnel for weight loss
		} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'l') {
			return `L_INTAKE`;
		} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'ms') {
			return `MS_INTAKE`;
		} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'fhrt') {
			return `FHRT_INTAKE`;
		} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'sc') {
			return `SC_INTAKE`;
		} else if (isStringNotEmpty(programSlug) && toLower(programSlug) === 'gmd') {
			return `GMD_INTAKE`;
		} else {
			return 'INTAKE';
		}
	}, [programSlug, version]);

	const goalsSurvey = useMemo(() => {
		if (isListNotEmpty(surveys) && isStringNotEmpty(programSlug)) {
			const configuredSurvey = getSurveyByEffectiveSurveyFlow({ program, product, productFamilies, surveys, flowKey: SURVEY_FLOW_KEYS.GOALS });
			if (isObjectNotEmpty(configuredSurvey)) {
				return configuredSurvey;
			}

			if (toLower(programSlug) === 'wl') {
				return getSurveyBySlug('weight-loss-goals2', surveys)
			} else if (toLower(programSlug) === 'l') {
				return getSurveyBySlug('longevity-goals', surveys)
			} else if (toLower(programSlug) === 'ms') {
				return getSurveyBySlug('metabolic-support-goals', surveys);
			} else if (toLower(programSlug) === 'fhrt') {
				return getSurveyBySlug('fhrt-goals', surveys);
			} else if (toLower(programSlug) === 'sc') {
				return getSurveyBySlug('sc-goals', surveys);
			} else if (toLower(programSlug) === 'gmd') {
				return getSurveyBySlug('gmd-goals', surveys);
			}
		}

		return null;
	}, [program, product, productFamilies, programSlug, surveys]);

	const intakeSurvey = useMemo(() => {
		if (isListNotEmpty(surveys) && isStringNotEmpty(programSlug)) {
			const configuredSurvey = getSurveyByEffectiveSurveyFlow({ program, product, productFamilies, surveys, flowKey: SURVEY_FLOW_KEYS.INTAKE });
			if (isObjectNotEmpty(configuredSurvey)) {
				return configuredSurvey;
			}

			if (toLower(programSlug) === 'wl') {
				return getSurveyBySlug('weight-loss-qualifying-form', surveys);
			}
			if (toLower(programSlug) === 'l') {
				return getSurveyBySlug('longevity-form4', surveys);
			}
			if (toLower(programSlug) === 'ms' && isStringNotEmpty(productSku)) {
				return (toLower(productSku) === 'mic-b12' || toLower(productSku) === 'b12')
					? getSurveyBySlug('metabolic-support-form-mic-b12', surveys)
					: getSurveyBySlug('metabolic-support-form2', surveys);
			}
			if (toLower(programSlug) === 'fhrt') {
				return getSurveyBySlug('fhrt-intake', surveys);
			}
			if (toLower(programSlug) === 'sc') {
				return getSurveyBySlug('sc-intake', surveys);
			}
			if (toLower(programSlug) === 'gmd') {
				return getSurveyBySlug('gmd-intake', surveys);
			}
		}

		return null;
	}, [program, product, productFamilies, productSku, programSlug, surveys]);

	const leadForms = useMemo(() => {
		let list = isListNotEmpty(lead?.forms) ? [...lead?.forms] : [];
		return orderBy(list, ['createdAt'], ['desc']);
	}, [lead?.forms]);
	// console.log('lead?.forms', lead?.forms);
	// console.log('lead', lead);
	// console.log('leadForms', leadForms);

	const goalsForm = useMemo(() => {
		return leadForms.find(i => i.surveyId === goalsSurvey?.id);
	}, [goalsSurvey?.id, leadForms]);
	// console.log('goalsForm', goalsForm);

	const intakeForm = useMemo(() => {
		return leadForms.find(i => i.surveyId === intakeSurvey?.id);
	}, [intakeSurvey?.id, leadForms]);
	// console.log('intakeForm', intakeForm);

	const userDocuments = useMemo(() => {
		return isListNotEmpty(profile?.documents) ? [...profile.documents] : [];
	}, [profile?.documents]);

	const leadDocuments = useMemo(() => {
		return isListNotEmpty(lead?.documents) ? [...lead.documents] : [];
	}, [lead?.documents]);

	const hasGLP1MedicationPhoto = useMemo(() => {
		return leadDocuments?.some(i => i.documentType === 'MEDICATION');
	}, [leadDocuments]);

	const availableProducts = useMemo(() => {
		if (isStringNotEmpty(programSlug) && isListNotEmpty(products) && isListNotEmpty(programs)) {
			let searchProgramSlug = ['l', 'ms'].includes(programSlug) ? ['l', 'ms'] : [programSlug];
			const programProducts = getProductsByProgramSlugs(searchProgramSlug, products, programs);

			return programProducts.filter(i => isProductEligibleForFunnel(i, { shippingState, testMode }));
		}

		return [];
	}, [programSlug, programs, products, shippingState, testMode]);

	const availableProductSkus = useMemo(() => {
		if (isListNotEmpty(availableProducts)) {
			return availableProducts.map(i => i.sku);
		}

		return [];
	}, [availableProducts]);

	const defaultVariant = useMemo(() => {
		if (isStringNotEmpty(product?.id)) {
			let productVariants = variants.filter(i => i.productId === product?.id);
			let availableVariants = productVariants?.filter(i => isBooleanTrue(i.enabled));
			let orderedVariants = orderBy(availableVariants, ['displayOrder'], ['asc']);
			if (isListNotEmpty(orderedVariants)) {
				return new Variant(orderedVariants[0]).toDto();
			}
		}

		return null;
	}, [product?.id, variants]);

	const selectedVariant = useMemo(() => {
		// console.log('selectedVariant memo', { selectedProduct, selectedVariantSku }, false);
		if (isObjectNotEmpty(selectedProduct) && isListNotEmpty(variants) && isStringNotEmpty(selectedVariantSku)) {
			return new Variant(variants.find(i => i.productId === selectedProduct?.id && isBooleanTrue(i.enabled) && i.sku === selectedVariantSku));
		}

		return null;
	}, [selectedProduct, selectedVariantSku, variants]);

	const defaultProduct = useMemo(() => {
		if ([15, 32, 33, 17, 70, 71, 72].includes(activeStep)) {
			let currentIntakeAnswers = isListNotEmpty(intakeAnswers) ? [...intakeAnswers] : [];
			const currentlyTakingGLP1MedicationField = currentIntakeAnswers.find(i => i.customName === 'currentlyTakingGLP1Medication');
			// console.log('currentlyTakingGLP1MedicationField', currentlyTakingGLP1MedicationField);
			if (isObjectNotEmpty(currentlyTakingGLP1MedicationField) && currentlyTakingGLP1MedicationField?.value === 'YES') {
				// is on a GLP-1 medication already so figure out what level to start them at
				const currentGLP1MedicationField = currentIntakeAnswers.find(i => i.customName === 'currentGLP1Medication');
				// console.log('currentGLP1MedicationField', currentGLP1MedicationField);
				if (isObjectNotEmpty(currentGLP1MedicationField) && ['COMPOUNDED_SEMAGLUTIDE', 'OZEMPIC_SEMAGLUTIDE', 'WEGOVY_SEMAGLUTIDE', 'RYBELSUS_SEMAGLUTIDE'].includes(currentGLP1MedicationField?.value)) {
					// current medication is a semaglutide
					if (availableProducts.some(i => i.slug === 'semaglutide-g')) {
						return getProductBySlug('semaglutide-g', availableProducts);
					} else if (availableProducts.some(i => i.slug === 'ozempic')) {
						return getProductBySlug('ozempic', availableProducts);
					}
				} else if (isObjectNotEmpty(currentGLP1MedicationField) && ['COMPOUNDED_TIRZEPATIDE', 'ZEPBOUND_TIRZEPATIDE', 'MOUNJARO_TIRZEPATIDE'].includes(currentGLP1MedicationField?.value)) {
					// current medication is a tirzepatide
					if (availableProducts.some(i => i.slug === 'zepbound')) {
						return getProductBySlug('zepbound', availableProducts);
					} else if (availableProducts.some(i => i.slug === 'tirzepatide-g')) {
						return getProductBySlug('tirzepatide', availableProducts);
					}
				}

				return null;
			} else {
				if (isFoundInCartBySku('SEMAGLUTIDE-G') && availableProducts.some(i => i.slug === 'semaglutide-g')) {
					return getProductBySlug('semaglutide-g', availableProducts);
				} else if (isFoundInCartBySku('SEMAGLUTIDE-GM') && availableProducts.some(i => i.slug === 'semaglutide-g')) {
					return getProductBySlug('semaglutide-g', availableProducts);
				} else if (isFoundInCartBySku('SEMAGLUTIDE') && availableProducts.some(i => i.slug === 'semaglutide-g')) {
					return getProductBySlug('semaglutide-g', availableProducts);
				} else if (isFoundInCartBySku('TIRZEPATIDE-G') && availableProducts.some(i => i.slug === 'tirzepatide-g')) {
					return getProductBySlug('tirzepatide-g', availableProducts);
				} else if (isFoundInCartBySku('TIRZEPATIDE-GM') && availableProducts.some(i => i.slug === 'tirzepatide-g')) {
					return getProductBySlug('tirzepatide-g', availableProducts);
				} else if (isFoundInCartBySku('TIRZEPATIDE') && availableProducts.some(i => i.slug === 'tirzepatide-g')) {
					return getProductBySlug('tirzepatide-g', availableProducts);
				} else if (isFoundInCartBySku('ZEPBOUND') && availableProducts.some(i => i.slug === 'zepbound')) {
					return getProductBySlug('zepbound', availableProducts);
				} else if (isFoundInCartBySku('ZEPBOUND') && availableProducts.some(i => i.slug === 'tirzepatide-g')) {
					return getProductBySlug('tirzepatide-g', availableProducts);
				} else if (isFoundInCartBySku('SEMAGLUTIDE-GM') && availableProducts.some(i => i.slug === 'semaglutide-g')) {
					return getProductBySlug('semaglutide-g', availableProducts);
				} else if (isFoundInCartBySku('SEMAGLUTIDE-GM') && availableProducts.some(i => i.slug === 'ozempic')) {
					return getProductBySlug('ozempic', availableProducts);
				} else if (isFoundInCartBySku('OZEMPIC') && availableProducts.some(i => i.slug === 'ozempic')) {
					return getProductBySlug('ozempic', availableProducts);
				} else if (isFoundInCartBySku('OZEMPIC') && availableProducts.some(i => i.slug === 'semaglutide-g')) {
					return getProductBySlug('semaglutide-g', availableProducts);
				} else if (availableProducts.some(i => i.slug === 'semaglutide-g')) {
					return getProductBySlug('semaglutide-g', availableProducts);
				} else if (availableProducts.some(i => i.slug === 'tirzepatide-g')) {
					return getProductBySlug('tirzepatide-g', availableProducts);
				} else if (availableProducts.some(i => i.slug === 'zepbound')) {
					return getProductBySlug('zepbound', availableProducts);
				} else if (availableProducts.some(i => i.slug === 'ozempic')) {
					return getProductBySlug('ozempic', availableProducts);
				} else {
					return null;
				}
			}
		} else {
			return null;
		}
	}, [intakeAnswers, availableProducts, activeStep, leadDocuments, cartItems]);

	const productInCart = useMemo(() => {
		return product;
	}, [product]);

	const titrationDirection = useMemo(() => {
		let currentIntakeAnswers = isListNotEmpty(intakeAnswers) ? [...intakeAnswers] : [];

		let nextDosageField = currentIntakeAnswers.find(i => i.customName === 'nextSemaglutideDosage');
		if (isObjectNotEmpty(nextDosageField) && isStringNotEmpty(nextDosageField.value)) {
			return nextDosageField.value;
		} else {
			nextDosageField = currentIntakeAnswers.find(i => i.customName === 'nextTirzepatideDosage');
			if (isObjectNotEmpty(nextDosageField) && isStringNotEmpty(nextDosageField.value)) {
				return nextDosageField.value;
			}
		}

		return null;
	}, [intakeAnswers]);

	const defaultShippingMethod = useMemo(() => {
		return getShippingMethodById('4hQSP5A82n7ZQTwqZ7gw', shippingMethods); // free expedited shipping method
	}, [shippingMethods]);

	const isCCFieldsValid = useMemo(() => {
		// console.log('cc errors', nmiErrors);
		return nmiErrors?.length === 0;
	}, [nmiErrors]);

	const subscriptionPeriodInDays = useMemo(() => {
		if (isObjectNotEmpty(product)) {
			let selectedProductObj = new Product(product);

			switch (selectedProductObj.subscriptionPeriod) {
				case 'YEAR':
					return 365 * selectedProductObj.subscriptionPeriodInterval * selectedFrequency;
				case 'MONTH':
					return 30 * selectedProductObj.subscriptionPeriodInterval * selectedFrequency;
				case 'WEEK':
					return 7 * selectedProductObj.subscriptionPeriodInterval * selectedFrequency;
				case 'DAY':
					return 1 * selectedProductObj.subscriptionPeriodInterval * selectedFrequency;
				default:
					return 1 * selectedProductObj.subscriptionPeriodInterval * selectedFrequency;
			}
		}

		return 0;
	}, [product, selectedFrequency]);

	const prescriptionPeriodInDays = useMemo(() => {
		if (isObjectNotEmpty(product)) {
			let selectedProductObj = new Product(product);

			switch (selectedProductObj.subscriptionPeriod) {
				case 'YEAR':
					return 365 * selectedProductObj.subscriptionPeriodInterval;
				case 'MONTH':
					return 30 * selectedProductObj.subscriptionPeriodInterval;
				case 'WEEK':
					return 7 * selectedProductObj.subscriptionPeriodInterval;
				case 'DAY':
					return 1 * selectedProductObj.subscriptionPeriodInterval;
				default:
					return 1 * selectedProductObj.subscriptionPeriodInterval;
			}
		}

		return 0;
	}, [product]);

	//#endregion
	//#region disqualified checkers

	const isStateDisqualified = useCallback((stateCode) => {
		if (isStringNotEmpty(stateCode)) {
			if (isStringNotEmpty(programSlug) && isListNotEmpty(products) && isListNotEmpty(programs) && (isStringNotEmpty(productSku) || toLower(programSlug) === 'wl')) {
				const targetProgram = getProgramBySlug(programSlug, programs);
				const programProductsList = getProductsByProgramSlug(programSlug, products, programs);
				const availableProductsList = programProductsList.filter(i => isProductEligibleForFunnel(i, { shippingState: stateCode, testMode }));
				if (isListEmpty(availableProductsList)) {
					return true;
				}

				const resolvedContext = resolveOfferContext({
					requestedProgramSlug: programSlug,
					requestedProductValue: productSku,
					products,
					programs,
					offerGroups
				});
				const choiceSet = getFunnelChoiceSetForEntry({
					programId: targetProgram?.id ?? resolvedContext.program?.id ?? null,
					program: targetProgram ?? resolvedContext.program ?? null,
					offerGroupId: resolvedContext.offerGroupId,
					choiceSets: funnelChoiceSets
				});
				const resolvedProductForState = getCanonicalProductForChoiceSetOfferGroup({
					choiceSet,
					offerGroupId: resolvedContext.offerGroupId ?? choiceSet?.defaultOfferGroupId,
					requestedProductValue: productSku,
					program: targetProgram ?? resolvedContext.program ?? null,
					products,
					shippingState: stateCode,
					testMode
				});

				if (['l', 'ms', 'fhrt', 'sc', 'gmd'].includes(toLower(programSlug)) && isStringNotEmpty(productSku) && !isObjectNotEmpty(resolvedProductForState)) {
					return true;
				}
			}
		}

		return false;
	}, [funnelChoiceSets, offerGroups, programSlug, products, programs, productSku, testMode]);

	const isAgeDisqualified = (dateOfBirth, productSku) => {
		if (isStringNotEmpty(productSku) && ['SERMORELIN', 'SERMORELIN-ODT'].includes(toUpper(productSku))) {
			return isAgeUnder20(dateOfBirth);
		}

		return isAgeUnder18(dateOfBirth);
	};

	const isIntakeDisqualified = (allIntakeAnswers = []) => {
		if (['l', 'ms', 'fhrt'].includes(toLower(programSlug))) {
			for (const intakeAnswer of allIntakeAnswers) {
				if (intakeAnswer.customName === 'otherMedications') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'familyPlanning') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'existingMedicalConditions') {
					let existingMedicalConditionDisqualifiers = ['CANCER'];
					if ((isArray(intakeAnswer?.value) && isListNotEmpty(intakeAnswer?.value) && intakeAnswer?.value.some(i => existingMedicalConditionDisqualifiers.includes(i))) || (isString(intakeAnswer?.value) && intakeAnswer?.value === 'YES')) {
						return true;
					}
				} else if (intakeAnswer.customName === 'cancer') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'currentlyPregnant') {
					if (intakeAnswer.value !== 'NONE') {
						return true;
					}
				}
			}
		} else if (toLower(programSlug) == "wl") {
			for (const intakeAnswer of allIntakeAnswers) {
				if (intakeAnswer.customName === 'otherMedications') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'allergyToGLP1') {
					let allergyToGLP1Disqualifiers = ['OZEMPIC_SEMAGLUTIDE', 'MOUNJARO_TIRZEPATIDE', 'WEGOVY_SEMAGLUTIDE', 'ZEPBOUND_TIRZEPATIDE', 'SAXENDA_LIRAGLUTIDE', 'TRULICITY_DULAGLUTIDE'];
					if (isListNotEmpty(intakeAnswer?.value) && intakeAnswer?.value.some(i => allergyToGLP1Disqualifiers.includes(i))) {
						return true;
					}
				} else if (intakeAnswer.customName === 'existingMedicalConditions') {
					let existingMedicalConditionDisqualifiers = ['ALLERGY_TO_GLP1', 'GASTROPARESIS_PARALYSIS_OF_YOUR_INTESTINES', 'TRIGLYCERIDES_OVER_600_AT_ANY_POINT', 'PANCREATITIS_OR_PANCREATIC_CANCER', 'TYPE_1_DIABETES', 'HYPOGLYCEMIA_LOW_BLOOD_SUGAR', 'INSULIN_DEPENDENT_DIABETES', 'THYROID_CANCER_OR_FAMILY_HISTORY_OF_THYROID_CANCER', 'PERSONAL_OR_FAMILY_HISTORY_OF_MULTIPLE_ENDOCRINE_NEOPLASIA_MEN_2_SYNDROME', 'EATING_DISORDER_ANOREXIA_OR_BULIMIA', 'CURRENT_SYMPTOMATIC_GALLSTONES'];
					if (isListNotEmpty(intakeAnswer?.value) && intakeAnswer?.value.some(i => existingMedicalConditionDisqualifiers.includes(i))) {
						return true;
					}
				} else if (intakeAnswer.customName === 'triglyceridesFollowUp') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'gastricBypass') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'currentlyPregnant') {
					if (intakeAnswer.value !== 'NONE') {
						return true;
					}
				}
			}
		} else if (toLower(programSlug) == "gmd") {
			for (const intakeAnswer of allIntakeAnswers) {
				if (intakeAnswer.customName === 'otherMedications') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'allergyToGLP1') {
					let allergyToGLP1Disqualifiers = ['OZEMPIC_SEMAGLUTIDE', 'MOUNJARO_TIRZEPATIDE', 'WEGOVY_SEMAGLUTIDE', 'ZEPBOUND_TIRZEPATIDE', 'SAXENDA_LIRAGLUTIDE', 'TRULICITY_DULAGLUTIDE'];
					if (isListNotEmpty(intakeAnswer?.value) && intakeAnswer?.value.some(i => allergyToGLP1Disqualifiers.includes(i))) {
						return true;
					}
				} else if (intakeAnswer.customName === 'existingMedicalConditions') {
					let existingMedicalConditionDisqualifiers = [
						'ALLERGY_TO_GLP1',
						'ANOREXIA_OR_BULIMIA',
						'CURRENT_SYMPTOMATIC_GALLSTONES',
						'CHRONIC_KIDNEY_DISEASE_STAGE_3_OR_GREATER',
						'GASTROPARESIS_PARALYSIS_OF_YOUR_INTESTINES',
						'HYPOGLYCEMIA_LOW_BLOOD_SUGAR',
						'HISTORY_OF_PANCREATITIS',
						'PANCREATIC_CANCER',
						'LIVER_FAILURE_LIVER_CIRRHOSIS',
						'PERSONAL_OR_FAMILY_HISTORY_OF_MEDULLARY_THYROID_CARCINOMA_MTC',
						'PERSONAL_OR_FAMILY_HISTORY_OF_MULTIPLE_ENDOCRINE_NEOPLASIA_MEN_2_SYNDROME',
						'SYNDROME_OF_INAPPROPRIATE_ANTIDIURETIC_HORMONE',
						'TYPE_1_OR_INSULIN_DEPENDENT_DIABETES',
						'TYPE_2_DIABETES',
						'TRIGLYCERIDES_OVER_600_AT_ANY_POINT',
						'PANCREATITIS_OR_PANCREATIC_CANCER',
						'UNCONTROLLED_HEART_DISEASE'
					];
					if (isListNotEmpty(intakeAnswer?.value) && intakeAnswer?.value.some(i => existingMedicalConditionDisqualifiers.includes(i))) {
						return true;
					}
				} else if (intakeAnswer.customName === 'triglyceridesFollowUp') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'gastricBypass') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'currentlyPregnant') {
					if (intakeAnswer.value !== 'NONE') {
						return true;
					}
				}
			}
		} else if (['sc'].includes(toLower(programSlug))) {
			for (const intakeAnswer of allIntakeAnswers) {
				if (intakeAnswer.customName === 'otherMedications') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'familyPlanning') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'existingMedicalConditions') {
					let existingMedicalConditionDisqualifiers = [
						'DARK_SPOT_SWELLING_OR_PAIN',
						'VISION_EYE_PAIN_OR_UNDEREYE_MASS',
						'CURRENT_FACIAL_INFECTION_SHINGLES_OR_COLD_SORE',
						'SEVERE_SUNBURN_SENSITIVITY_TO_PHOTOSENSITIZING_MEDICATIONS'
					];
					if ((isArray(intakeAnswer?.value) && isListNotEmpty(intakeAnswer?.value) && intakeAnswer?.value.some(i => existingMedicalConditionDisqualifiers.includes(i))) || (isString(intakeAnswer?.value) && intakeAnswer?.value === 'YES')) {
						return true;
					}
				} else if (intakeAnswer.customName === 'cancer') {
					if (intakeAnswer.value === 'YES') {
						return true;
					}
				} else if (intakeAnswer.customName === 'currentlyPregnant') {
					if (intakeAnswer.value !== 'NONE') {
						return true;
					}
				}
			}
		}

		return false;
	};

	const isBMIDisqualified = (BMILocal, currentlyTakingGLP1Local = false) => {
		// console.log('BMI: ', BMILocal);
		// console.log('BMI Rounded: ', Number(BMI.toFixed(1)));
		// console.log('Currently taking GLP-1', isBooleanTrue(currentlyTakingGLP1Local), !isBooleanTrue(currentlyTakingGLP1Local));

		if (isNotNullOrUndefined(currentlyTakingGLP1Local)) {
			if ((Number(BMILocal.toFixed(1)) < 20 && isBooleanTrue(currentlyTakingGLP1Local)) || (Number(BMILocal.toFixed(1)) < 25 && !isBooleanTrue(currentlyTakingGLP1Local))) {
				return true;
			} else {
				return false;
			}
		} else {
			if (Number(BMILocal.toFixed(1)) < 25) {
				return true;
			} else {
				return false;
			}
		}
	};

	const isBMIMicrodosingDisqualified = (BMILocal) => {
		// console.log('BMI: ', BMILocal);
		// console.log('BMI Rounded: ', Number(BMI.toFixed(1)));

		if (Number(BMILocal.toFixed(1)) < 18) {
			return true;
		} else {
			return false;
		}
	};

	const isCurrentlyPregnantDisqualified = (currentlyPregnant, gender) => {
		return isStringNotEmpty(currentlyPregnant) && currentlyPregnant !== 'NONE' && gender === 'FEMALE';
	};

	const checkIfActiveIntakeStepQuestionsHaveAnswers = useCallback(() => {
		// let activeStepQuestions = intakeSurvey.questions.filter(i => i.parentId && i.parentId === activeIntakeStep?.id);
		// console.log('activeStepQuestions', activeStepQuestions);
		let activeStepQuestionCustomNames = intakeSurvey?.questions?.filter(i => i.customName && i.parentId && i.parentId === activeIntakeStep?.id).map((question) => question.customName);
		// console.log('activeStepQuestionCustomNames', activeStepQuestionCustomNames);
		let activeStepAnswers = intakeAnswers?.filter(i => i.customName && activeStepQuestionCustomNames.includes(i.customName));
		let haveAnswers = activeStepAnswers.some(i => i.value !== undefined);
		// console.log('checkIfActiveIntakeStepQuestionsHaveAnswers', haveAnswers);
		return haveAnswers;
	}, [intakeSurvey, intakeAnswers, activeIntakeStep]);

	//#endregion

	return {
		addresses: useSelector((appState) => appState.funnelState.addresses),
		setAddresses: (val) => dispatch(setAddresses(val)),
		selectedAddress: useSelector((appState) => appState.funnelState.selectedAddress),
		setSelectedAddress: (val) => dispatch(setSelectedAddress(val)),
		showAddressesModal: useSelector((appState) => appState.funnelState.showAddressesModal),
		setShowAddressesModal: (val) => dispatch(setShowAddressesModal(val)),
		showNewAddressModal: useSelector((appState) => appState.funnelState.showNewAddressModal),
		setShowNewAddressModal: (val) => dispatch(setShowNewAddressModal(val)),
		showEditAddressModal: useSelector((appState) => appState.funnelState.showEditAddressModal),
		setShowEditAddressModal: (val) => dispatch(setShowEditAddressModal(val)),

		activeStep: useSelector((appState) => appState.funnelState.activeStep),
		setActiveStep: (val) => dispatch(setActiveStep(val)),
		currentSection: useSelector((appState) => appState.funnelState.currentSection),
		setCurrentSection: (val) => dispatch(setCurrentSection(val)),
		completedSections: useSelector((appState) => appState.funnelState.completedSections),
		setCompletedSections: (val) => dispatch(setCompletedSections(val)),
		goalsAnswers: useSelector((appState) => appState.funnelState.goalsAnswers),
		setGoalsAnswers: (val) => dispatch(setGoalsAnswers(val)),
		intakeAnswers: useSelector((appState) => appState.funnelState.intakeAnswers),
		setIntakeAnswers: (val) => dispatch(setIntakeAnswers(val)),

		weightLossAnswers: useSelector((appState) => appState.funnelState.weightLossAnswers),
		setWeightLossAnswers: (val) => dispatch(setWeightLossAnswers(val)),
		currentlyPregnant: useSelector((appState) => appState.funnelState.currentlyPregnant),
		setCurrentlyPregnant: (val) => dispatch(setCurrentlyPregnant(val)),
		cutoffWeight: useSelector((appState) => appState.funnelState.cutoffWeight),
		setCutoffWeight: (val) => dispatch(setCutoffWeight(val)),
		currentlyTakingGLP1: useSelector((appState) => appState.funnelState.currentlyTakingGLP1),
		setCurrentlyTakingGLP1: (val) => dispatch(setCurrentlyTakingGLP1(val)),
		wantsAlternativeDosing: useSelector((appState) => appState.funnelState.wantsAlternativeDosing),
		setWantsAlternativeDosing: (val) => dispatch(setWantsAlternativeDosing(val)),
		experiencesSideEffects: useSelector((appState) => appState.funnelState.experiencesSideEffects),
		setExperiencesSideEffects: (val) => dispatch(setExperiencesSideEffects(val)),
		avoidSideEffects: useSelector((appState) => appState.funnelState.avoidSideEffects),
		setAvoidSideEffects: (val) => dispatch(setAvoidSideEffects(val)),

		maleEffects: useSelector((appState) => appState.funnelState.weightLossAnswers?.maleEffects),
		setMaleEffects: (val) => dispatch(updateWeightLossAnswers({ maleEffects: val })),
		femaleEffects: useSelector((appState) => appState.funnelState.weightLossAnswers?.femaleEffects),
		setFemaleEffects: (val) => dispatch(updateWeightLossAnswers({ femaleEffects: val })),
		priority: useSelector((appState) => appState.funnelState.weightLossAnswers?.priority),
		setPriority: (val) => dispatch(updateWeightLossAnswers({ priority: val })),
		bodyShape: useSelector((appState) => appState.funnelState.weightLossAnswers?.bodyShape),
		setBodyShape: (val) => dispatch(updateWeightLossAnswers({ bodyShape: val })),
		targetWeight: useSelector((appState) => appState.funnelState.weightLossAnswers?.targetWeight),
		setTargetWeight: (val) => dispatch(updateWeightLossAnswers({ targetWeight: val })),
		primaryGoal: useSelector((appState) => appState.funnelState.weightLossAnswers?.primaryGoal),
		setPrimaryGoal: (val) => dispatch(updateWeightLossAnswers({ primaryGoal: val })),
		daySpent: useSelector((appState) => appState.funnelState.weightLossAnswers?.daySpent),
		setDaySpent: (val) => dispatch(updateWeightLossAnswers({ daySpent: val })),
		activityLevel: useSelector((appState) => appState.funnelState.weightLossAnswers?.activityLevel),
		setActivityLevel: (val) => dispatch(updateWeightLossAnswers({ activityLevel: val })),
		metabolicRate: useSelector((appState) => appState.funnelState.weightLossAnswers?.metabolicRate),
		setMetabolicRate: (val) => dispatch(updateWeightLossAnswers({ metabolicRate: val })),
		sleepQuality: useSelector((appState) => appState.funnelState.weightLossAnswers?.sleepQuality),
		setSleepQuality: (val) => dispatch(updateWeightLossAnswers({ sleepQuality: val })),
		sleepLength: useSelector((appState) => appState.funnelState.weightLossAnswers?.sleepLength),
		setSleepLength: (val) => dispatch(updateWeightLossAnswers({ sleepLength: val })),
		mindset: useSelector((appState) => appState.funnelState.weightLossAnswers?.mindset),
		setMindset: (val) => dispatch(updateWeightLossAnswers({ mindset: val })),



		intakeSteps: useSelector((appState) => appState.funnelState.intakeSteps),
		setIntakeSteps: (val) => dispatch(setIntakeSteps(val)),
		activeIntakeStep: useSelector((appState) => appState.funnelState.activeIntakeStep),
		setActiveIntakeStep: (val) => dispatch(setActiveIntakeStep(val)),

		goalsSteps: useSelector((appState) => appState.funnelState.goalsSteps),
		setGoalsSteps: (val) => dispatch(setGoalsSteps(val)),
		activeGoalsStep: useSelector((appState) => appState.funnelState.activeGoalsStep),
		setActiveGoalsStep: (val) => dispatch(setActiveGoalsStep(val)),

		showSignInModal: useSelector((appState) => appState.funnelState.showSignInModal),
		setShowSignInModal: (val) => dispatch(setShowSignInModal(val)),
		showMoreInfoModal: useSelector((appState) => appState.funnelState.showMoreInfoModal),
		setShowMoreInfoModal: (val) => dispatch(setShowMoreInfoModal(val)),
		showWeightLossDisclaimerModal: useSelector((appState) => appState.funnelState.showWeightLossDisclaimerModal),
		setShowWeightLossDisclaimerModal: (val) => dispatch(setShowWeightLossDisclaimerModal(val)),

		productSku: useSelector((appState) => appState.funnelState.productSku),
		setProductSku: (val) => dispatch(setProductSku(val)),
		selectedProduct: useSelector((appState) => appState.funnelState.selectedProduct),
		setSelectedProduct: (val) => dispatch(setSelectedProduct(val)),
		selectedFrequency: useSelector((appState) => appState.funnelState.selectedFrequency),
		setSelectedFrequency: (val) => dispatch(setSelectedFrequency(val)),

		availableProductSkusList: useSelector((appState) => appState.funnelState.availableProductSkus),
		setAvailableProductSkusList: (val) => dispatch(setAvailableProductSkus(val)),
		unenrolledProductSkus: useSelector((appState) => appState.funnelState.unenrolledProductSkus),
		setUnenrolledProductSkus: (val) => dispatch(setUnenrolledProductSkus(val)),

		selectedVariantSku: useSelector((appState) => appState.funnelState.selectedVariantSku),
		setSelectedVariantSku: (val) => dispatch(setSelectedVariantSku(val)),
		startingLevel: useSelector((appState) => appState.funnelState.startingLevel),
		setStartingLevel: (val) => dispatch(setStartingLevel(val)),

		subscription: useSelector((appState) => appState.funnelState.subscription),
		setSubscription: (val) => dispatch(setSubscription(val)),
		encounter: useSelector((appState) => appState.funnelState.encounter),
		setEncounter: (val) => dispatch(setEncounter(val)),
		order: useSelector((appState) => appState.funnelState.order),
		setOrder: (val) => dispatch(setOrder(val)),

		userCouponCode: useSelector((appState) => appState.funnelState.userCouponCode),
		setUserCouponCode: (val) => dispatch(setUserCouponCode(val)),
		showPaymentScheduleModal: useSelector((appState) => appState.funnelState.showPaymentScheduleModal),
		setShowPaymentScheduleModal: (val) => dispatch(setShowPaymentScheduleModal(val)),
		billingSameAsShipping: useSelector((appState) => appState.funnelState.billingSameAsShipping),
		setBillingSameAsShipping: (val) => dispatch(setBillingSameAsShipping(val)),
		paymentMethods: useSelector((appState) => appState.funnelState.paymentMethods),
		setPaymentMethods: (val) => dispatch(setPaymentMethods(val)),
		paymentMethodType: useSelector((appState) => appState.funnelState.paymentMethodType),
		setPaymentMethodType: (val) => dispatch(setPaymentMethodType(val)),
		showKlarnaPaymentMethod: useSelector((appState) => appState.funnelState.showKlarnaPaymentMethod),
		setShowKlarnaPaymentMethod: (val) => dispatch(setShowKlarnaPaymentMethod(val)),

		isFunnelLoading: useSelector((appState) => appState.funnelState.isFunnelLoading),
		setIsFunnelLoading: (val) => dispatch(setIsFunnelLoading(val)),
		isFunnelLoadingMessage: useSelector((appState) => appState.funnelState.isFunnelLoadingMessage),
		setIsFunnelLoadingMessage: (val) => dispatch(setIsFunnelLoadingMessage(val)),
		isAccountLoading: useSelector((appState) => appState.funnelState.isAccountLoading),
		setIsAccountLoading: (val) => dispatch(setIsAccountLoading(val)),
		isAccountLoadingMessage: useSelector((appState) => appState.funnelState.isAccountLoadingMessage),
		setIsAccountLoadingMessage: (val) => dispatch(setIsAccountLoadingMessage(val)),
		isEligibilityLoading: useSelector((appState) => appState.funnelState.isEligibilityLoading),
		setIsEligibilityLoading: (val) => dispatch(setIsEligibilityLoading(val)),
		isEligibilityLoadingMessage: useSelector((appState) => appState.funnelState.isEligibilityLoadingMessage),
		setIsEligibilityLoadingMessage: (val) => dispatch(setIsEligibilityLoadingMessage(val)),
		isAddressLoading: useSelector((appState) => appState.funnelState.isAddressLoading),
		setIsAddressLoading: (val) => dispatch(setIsAddressLoading(val)),
		addressError: useSelector((appState) => appState.funnelState.addressError),
		setAddressError: (val) => dispatch(setAddressError(val)),
		funnelError: useSelector((appState) => appState.funnelState.funnelError),
		setFunnelError: (val) => dispatch(setFunnelError(val)),

		programSlug: useSelector((appState) => appState.funnelState.programSlug),
		setProgramSlug: (val) => dispatch(setProgramSlug(val)),
		addon: useSelector((appState) => appState.funnelState.addon),
		setAddon: (val) => dispatch(setAddon(val)),
		version: useSelector((appState) => appState.funnelState.version),
		setVersion: (val) => dispatch(setVersion(val)),

		resetFunnel: () => dispatch(resetFunnel()),

		programId: programId,
		program: program,

		campaign: campaign,
		campaignId: campaignId,
		campaignSlug: campaignSlug,

		productCategory: productCategory,
		productCategoryId: productCategoryId,
		productCategorySlug: productCategorySlug,

		product: product,
		productId: productId,

		funnelName: funnelName,
		programProducts: programProducts,
		goalsSurvey: goalsSurvey,
		intakeSurvey: intakeSurvey,
		leadForms: leadForms,
		goalsForm: goalsForm,
		intakeForm: intakeForm,
		leadDocuments: leadDocuments,
		userDocuments: userDocuments,
		availableProducts: availableProducts,
		availableProductSkus: availableProductSkus,
		defaultVariant: defaultVariant,
		selectedVariant: selectedVariant,

		isStateDisqualified: isStateDisqualified,
		isAgeDisqualified: isAgeDisqualified,
		isIntakeDisqualified: isIntakeDisqualified,
		isBMIDisqualified: isBMIDisqualified,
		isBMIMicrodosingDisqualified: isBMIMicrodosingDisqualified,
		isCurrentlyPregnantDisqualified: isCurrentlyPregnantDisqualified,

		checkIfActiveIntakeStepQuestionsHaveAnswers: checkIfActiveIntakeStepQuestionsHaveAnswers,

		onChangeHeight: (val) => dispatch(onChangeHeight(val)),
		onChangeWeight: (val) => dispatch(onChangeWeight(val)),
		onChangeVariantSku: (variantSku) => dispatch(onChangeVariantSku(variantSku)),
		onChangeProduct: (productSku) => dispatch(onChangeProduct(productSku)),
		onChangeFrequency: (frequency, productId) => dispatch(onChangeFrequency(frequency, productId)),

		defaultProduct: defaultProduct,
		productInCart: productInCart,
		titrationDirection: titrationDirection,

		getNextCompoundedSemaglutideDosage: getNextCompoundedSemaglutideDosage,
		getNextCompoundedSemaglutideGMDosage: getNextCompoundedSemaglutideGMDosage,
		getNextOzempicDosage: getNextOzempicDosage,
		getNextCompoundedTirzepatideDosage: getNextCompoundedTirzepatideDosage,
		getNextCompoundedTirzepatideGMDosage: getNextCompoundedTirzepatideGMDosage,
		getNextZepboundDosage: getNextZepboundDosage,

		hasGLP1MedicationPhoto: hasGLP1MedicationPhoto,

		profile: profile,
		shippingState: shippingState,
		gender: gender,
		dateOfBirth: dateOfBirth,
		height: height,
		weight: weight,
		BMI: BMI,
		firstName: firstName,
		lastName: lastName,
		phoneVerified: phoneVerified,
		currentAuthUser: currentAuthUser,

		programs: programs,
		shippingMethods: shippingMethods,
		products: products,
		variants: variants,
		productFamilies: productFamilies,
		offerGroups: offerGroups,
		funnelChoiceSets: funnelChoiceSets,
		productCategories: productCategories,
		campaigns: campaigns,
		surveys: surveys,

		cartItems: cartItems,
		coupon: coupon,
		couponApplied: couponApplied,
		couponCodeApplied: couponCodeApplied,
		shippingAddress: shippingAddress,
		billingAddress: billingAddress,
		taxRate: taxRate,
		cartSummary: cartSummary,
		shippingMethod: shippingMethod,
		paymentMethod: paymentMethod,

		defaultShippingMethod: defaultShippingMethod,

		paymentTokenResponse: useSelector((appState) => appState.collectJSState.paymentTokenResponse),
		collectJS: useSelector((appState) => appState.collectJSState.collectJS),
		nmiErrors: useSelector((appState) => appState.collectJSState.errors),
		isCCFieldsAvailable: useSelector((appState) => appState.collectJSState.isCCFieldsAvailable),
		isCCFieldsValid: isCCFieldsValid,

		clientTokenResponse: useSelector((appState) => appState.klarnaState.clientTokenResponse),
		authorizationTokenResponse: useSelector((appState) => appState.klarnaState.authorizationTokenResponse),
		klarnaInstance: useSelector((appState) => appState.klarnaState.klarnaInstance),

		subscriptionPeriodInDays: subscriptionPeriodInDays,
		prescriptionPeriodInDays: prescriptionPeriodInDays
	};
};

export default funnelReducer;
